// video player page component

import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlay, faPause, faArrowLeft, faForward, faBackward, 
  faTimes, faCheck, faExclamationTriangle, faInfoCircle, 
  faThLarge, faList, faSearch, faSpinner, faShareAlt,
  faCopy, faChevronDown, faChevronUp, faStepForward, 
  faRedo, faFlag, faLightbulb 
} from '@fortawesome/free-solid-svg-icons';
import { MoviesAPI, ShowsAPI } from '../../api';
import Player from './Player';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import ContentSlider from '../Home/ContentSlider';
import ReportModal from './ReportModal';
import EpisodeSuggestionModal from './EpisodeSuggestionModal';
import disableDevtool from 'disable-devtool';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const PageLayoutContainer = styled.div`
  background-color: #000000;
  min-height: 100vh;
  color: var(--text-light);
  animation: ${fadeIn} 0.3s ease-in-out;
  overflow-x: hidden;
  padding-top: 90px; /* Increased from 80px to give more space for header */
`;

// mobile-desktop layout structure
const ContentGrid = styled.div`
  display: flex;
  flex-direction: column; /* Mobile: Player, Info, Episodes stacked */

  @media (min-width: 1024px) {
    flex-direction: row; /* Desktop: 3 columns */
    max-height: calc(100vh - 80px);
    gap: 1px;
  }
`;

// Episodes List
const LeftPanel = styled.div`
  order: 3; /* Mobile: appears last */
  background-color: #0a0a0a;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    order: 1; /* Desktop: appears first */
    flex: 0 0 340px; /* width*/
    max-height: 100%; /*height of contentGrid */
  }
`;

// Player + Source Info
const MiddlePanel = styled.div`
  order: 1; /* Mobile: Player appears first */
  display: flex;
  flex-direction: column;
  background-color: #000000; /* Player area background */
  flex: 1; /* Mobile: takes available space if others are fixed height */

  @media (min-width: 1024px) {
    order: 2; /* Desktop appears second */
    flex: 2; 
    max-height: 100%;
    overflow-y: auto;
    border-left: 1px solid rgba(255,255,255,0.05);
    border-right: 1px solid rgba(255,255,255,0.05);
    padding: 0 12px; /* Horizontal padding */
  }
`;

//Movie-episode Info
const RightPanel = styled.div`
  order: 2;
  background-color: #0a0a0a;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    order: 3;
    flex: 0 0 360px;
    max-height: 100%;
  }
`;


// Player
const PlayerWrapper = styled.div`
  background-color: #000000;
  position: relative;
  width: 100%;

  @media (min-width: 1024px) {
    padding-top: 20px; // space above player in middle column
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  padding-top: 56.25%; /*16:9*/
  position: relative;
  background-color: #000000;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  
  &::after {
    content: ''; display: block; clear: both;
  }
  
  @media (min-width: 1024px) {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 100px;
      background: linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%);
      z-index: 2;
      pointer-events: none;
    }
  }
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  z-index: 1;
  background-color: #000000;
`;

const NavigationButtons = styled.div`
  background-color: #0a0a0a;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0 0 8px 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    padding: 10px;
    margin-bottom: 8px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-light)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-light)'};
  padding: 8px 12px;
  border-radius: var(--border-radius);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.15)'};
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
  &:active { transform: translateY(0); }
  &:disabled { 
    opacity: 0.5; 
    cursor: not-allowed; 
    transform: none;
    box-shadow: none;
  }
  svg { font-size: 13px; }
`;

const BackButton = styled(NavButton)`
  margin-right: auto;
`;

const ShareButton = styled(NavButton)`
  background-color: rgba(75, 85, 99, 0.3);
  &:hover { background-color: rgba(75, 85, 99, 0.5); }
`;

// Details on Right
const EpisodeInfoSection = styled.div`
  padding: 15px;
  background-color: #0a0a0a;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 8px;
  box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.3);

  @media (min-width: 1024px) {
    overflow-y: auto;
    padding: 15px;
  }
  @media (max-width: 1023px) {
    border-radius: 0;
    padding: 12px;
  }
`;

//Sidebar
const EpisodeSidebarSection = styled.div`
  background-color: #0a0a0a; /* Retained */
  border-top: 1px solid rgba(255, 255, 255, 0.1); /* Mobile top border */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Retained */
  height: 100%; /* Fill LeftPanel */

  @media (max-width: 1023px) {
    min-height: 400px; 
    max-height: 500px;
  }
  
  @media (min-width: 1024px) {
    border-top: none; /* No top border on desktop as its a side panel */
  }
`;

const ContentTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 10px;
  color: var(--text-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) { 
    font-size: 18px;
    margin-bottom: 8px;
  }
`;

const WatchingInfo = styled.div`
  background-color: #111111;
  padding: 12px 15px;
  border-radius: var(--border-radius);
  margin-bottom: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  animation: ${fadeIn} 0.5s ease-out;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
`;

const WatchingTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--primary-light);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--primary-light);
    animation: ${pulse} 2s infinite;
  }
`;

const WatchingMessage = styled.div`
  font-size: 12px;
  color: var(--text-dim);
`;

const SourceSection = styled.div`
  margin-top: 15px;
  animation: ${fadeIn} 0.6s ease-out;
  padding: 15px;
  background-color: #0a0a0a;
  margin-bottom: 15px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  
  @media (min-width: 1024px) {
    padding: 15px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 768px) {
    margin-top: 10px;
    padding: 12px;
    margin-bottom: 10px;
  }
`;

const SourceTitle = styled.h3`
  font-size: 16px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  svg { color: var(--primary-light); }
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 8px;
  }
`;

const QualityButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 15px;
`;

const QualityButton = styled.button`
  background-color: ${props => props.$active ? 'var(--primary-color)' : '#1a1a1a'};
  color: ${props => props.$active ? '#fff' : 'var(--text-light)'};
  border: none;
  padding: 8px 15px;
  border-radius: var(--border-radius);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background-color: ${props => props.$active ? 'var(--primary-light)' : '#2a2a2a'};
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
  &:active { transform: translateY(0); }
  
  ${props => props.$active && css`
    animation: ${pulse} 0.3s ease-in-out;
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.3);
  `}
  
  @media (max-width: 768px) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

// playback Controls
const PlaybackControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 15px;
  padding: 12px 15px;
  background-color: #0a0a0a;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  
  @media (min-width: 1024px) {
    padding: 12px 15px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 768px) {
    margin-top: 8px;
    margin-bottom: 12px;
    padding: 10px 8px;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`;

const PlaybackControlButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.2)' : '#1a1a1a'};
  border: 1px solid ${props => props.$active ? 'var(--primary-light)' : 'transparent'};
  color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-light)'};
  padding: 8px 15px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 90px; /* Added minimum width */
  &:hover {
    background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.3)' : '#2a2a2a'};
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
  &:active { transform: translateY(0); }
  svg { font-size: 14px; margin-right: 5px; } /* Increased icon size */
  
  ${props => props.$active && css`
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.2);
  `}
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 11px;
    min-width: 70px;
    flex-shrink: 0;
    
    svg { 
      font-size: 11px; 
      margin-right: 3px;
    }
  }
`;

const ControlsGroup = styled.div`
  display: flex;
  gap: 10px;
  flex: 1; /* Added to give more space */
  
  @media (max-width: 768px) {
    gap: 6px;
    flex-wrap: wrap;
  }
`;

const SourceDropdown = styled.div`
  position: relative;
  
  @media (max-width: 768px) {
    width: auto;
    min-width: 90px;
    max-width: 110px;
    margin-left: auto;
    margin-top: 6px;
  }
`;

const SourceDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-light);
  padding: 8px 15px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  position: relative;
  
  &:hover {
    background-color: #2a2a2a;
    transform: translateY(-2px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  }
  
  svg {
    font-size: 12px;
    margin-left: 8px;
  }
  
  @media (max-width: 768px) {
    padding: 8px;
    font-size: 12px;
    min-width: auto;
    
    span {
      max-width: 70px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    svg {
      margin-left: 4px;
      flex-shrink: 0;
    }
  }
`;

const SourcesMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #1a1a1a;
  border-radius: var(--border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  margin-top: 5px;
  min-width: 130px;
  overflow-x: visible;
  
  @media (max-width: 768px) {
    width: 130px;
    left: auto;
    right: 0;
    transform: translateX(0); /* Ensure it doesn't go off-screen */
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #0a0a0a;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #333333;
    border-radius: 3px;
  }
`;

const SourceOption = styled.div`
  padding: 8px 15px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-light)'};
  background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.2)' : '#2a2a2a'};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  
  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: 12px;
  }
`;

const DubIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  z-index: 15;
  
  &:hover > div {
    opacity: 1;
    visibility: visible;
  }
`;

const UnofficialDubIcon = styled(FontAwesomeIcon)`
  color: var(--primary-light);
  margin-left: 8px;
  font-size: 14px;
  opacity: 0.8;
  cursor: help;
  
  &:hover {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-left: 6px;
  }
`;

const UnofficialDubTooltip = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  width: max-content;
  max-width: 200px;
  z-index: 100;
  top: 0;
  right: 100%;
  transform: translateY(-50%);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  margin-right: 8px;
  
  @media (max-width: 768px) {
    right: auto;
    left: 50%;
    top: -40px;
    transform: translateX(-50%);
    margin-right: 0;
  }
`;

const ReadMoreDescription = styled.div`
  color: var(--text-dim);
  line-height: 1.5;
  font-size: 14px;
  margin-bottom: 15px;
  position: relative;
  
  &::first-letter {
    font-size: 120%;
    font-weight: bold;
    color: var(--primary-light);
  }

  @media (max-width: 768px) {
    max-height: ${props => props.$expanded ? 'none' : '80px'};
    overflow: ${props => props.$expanded ? 'visible' : 'hidden'};
    
    ${props => !props.$expanded && props.$hasOverflow && css`
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 30px;
        background: linear-gradient(to bottom, rgba(10, 10, 10, 0), rgba(10, 10, 10, 1));
      }
    `}
  }
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-light);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-top: -10px;
  margin-bottom: 15px;
  display: block;
  
  &:hover {
    text-decoration: underline;
  }
`;

const EpisodeListHeader = styled.div`
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #0a0a0a;
`;

const EpisodeListTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
`;

const SearchBox = styled.div`
  position: relative;
  width: 160px;
  @media (max-width: 768px) { width: 130px; }
`;

// Modify the SearchInput component to add specific classnames and prevent propagation
const SearchInput = styled.input`
  width: 100%;
  padding: 6px 25px 6px 8px;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius);
  color: var(--text-light);
  font-size: 13px;
  &:focus {
    outline: none;
    border-color: var(--primary-light);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
  &::placeholder { color: rgba(255, 255, 255, 0.5); }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
`;

const EpisodeList = styled.div`
  background-color: #0a0a0a; /* Match EpisodeSidebarSection */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #0a0a0a; }
  &::-webkit-scrollbar-thumb { background-color: #333333; border-radius: 3px; }
  scrollbar-width: thin;
  scrollbar-color: #333333 #0a0a0a;

  @media (max-width: 1023px) { /* Mobile */
    height: 350px; 
    overflow-y: auto;
  }
  @media (min-width: 1024px) { /* Desktop */
    flex: 1; /* Takes remaining space in EpisodeSidebarSection */
    overflow-y: auto;
  }
`;

const EpisodeItem = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.$active ? '#1a1a1a' : props.$watched ? '#080808' : '#0a0a0a'};
  border-left: 3px solid ${props => props.$active ? 'var(--primary-light)' : props.$watched ? '#333' : 'transparent'};
  opacity: ${props => props.$watched ? 0.9 : 1};
  height: 56px;
  &:hover {
    background-color: ${props => props.$active ? '#1a1a1a' : props.$watched ? '#0f0f0f' : '#151515'};
    transform: translateX(2px);
  }
  
  ${props => props.$active && css`
    animation: ${pulse} 0.3s ease-in-out;
  `}
`;

const EpisodeNumber = styled.div`
  font-weight: 600;
  font-size: 16px;
  width: 30px;
  color: ${props => props.$active ? 'var(--primary-light)' : props.$watched ? 'var(--text-dim)' : 'var(--text-light)'};
`;

const EpisodeThumbnail = styled.div`
  width: 60px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  margin-right: 10px;
  position: relative;
  flex-shrink: 0; /* to prevent thumbnail from shrinking [fixed] */
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: ${props => props.$watched ? 'brightness(0.5) grayscale(0.7)' : 'none'};
    transition: all 0.3s;
  }
  ${EpisodeItem}:hover & img {
    transform: scale(1.05);
    filter: ${props => props.$watched ? 'brightness(0.6) grayscale(0.5)' : 'none'};
  }
`;

const WatchedOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 16px;
  svg { color: var(--success-color); filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.8)); }
`;

const EpisodeItemTitle = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${props => props.$watched ? 'var(--text-dim)' : 'inherit'};
  font-size: 14px;
  max-width: 170px; /* limited text width to prevent layout breaking */
  min-width: 0;
`;

const PlayButton = styled.div`
  width: 24px; height: 24px;
  border-radius: 50%;
  background-color: ${props => props.$active ? 'var(--primary-light)' : 'rgba(255, 255, 255, 0.2)'};
  display: flex; align-items: center; justify-content: center;
  margin-left: 10px;
  color: ${props => props.$active ? '#000' : 'var(--text-light)'};
  transition: all 0.2s;
  flex-shrink: 0; /* Prevent play button from shrinking */
  ${EpisodeItem}:hover & {
    transform: scale(1.2);
    background-color: var(--primary-light);
    color: #000;
  }
  svg { font-size: 12px; }
`;

const SeasonSelector = styled.div`
  display: flex;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background-color: #0a0a0a;
`;

const SeasonLabel = styled.div`
  font-weight: 600;
  margin-right: 15px;
`;

// Update to support dropdown style
const SeasonDropdown = styled.div`
  position: relative;
  background-color: #1a1a1a;
  border-radius: var(--border-radius);
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 100px;
  }
`;

const SeasonDropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-light);
  padding: 6px 15px;
  border-radius: var(--border-radius);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  
  &:hover {
    background-color: #2a2a2a;
  }
  
  svg {
    font-size: 12px;
    margin-left: 8px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const SeasonsMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #1a1a1a;
  border-radius: var(--border-radius);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  margin-top: 5px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #0a0a0a;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #333333;
    border-radius: 3px;
  }
`;

const SeasonOption = styled.div`
  padding: 8px 15px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-light)'};
  background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.1)' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(59, 130, 246, 0.2)' : '#2a2a2a'};
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
  }
`;

const CurrentSeason = styled.div`
  padding: 6px 15px;
  background-color: #222222;
  font-weight: 500;
`;

const Description = styled.div`
  color: var(--text-dim);
  line-height: 1.5;
  font-size: 14px;
  margin-bottom: 15px;
  
  &::first-letter {
    font-size: 120%;
    font-weight: bold;
    color: var(--primary-light);
  }

  @media (min-width: 1024px) {
    margin-bottom: 12px;
  }
`;

const SimilarContent = styled.div`
  overflow: hidden;
  background-color: #0a0a0a;
  flex-shrink: 0;
  border-radius: 0;
  
  .sliderTitle {
    font-size: 18px;
    margin-bottom: 10px;
  }
  
  .sliderContainer {
    padding-bottom: 5px;
  }
`;

// Enhance the LoadingContainer
const LoadingContainer = styled.div`
  display: flex; 
  flex-direction: column; 
  justify-content: center; 
  align-items: center;
  padding: 40px 0;
  color: var(--text-light); 
  font-size: 16px; 
  gap: 15px;
  
  svg { 
    font-size: 30px; 
    color: var(--primary-light); 
    animation: ${spin} 1s linear infinite; 
    filter: drop-shadow(0 0 5px rgba(59, 130, 246, 0.4));
  }
  
  &::after {
    content: 'Loading...';
    font-size: 14px;
    color: var(--text-dim);
  }
`;

// Enhance the ErrorMessage
const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.2);
  border: 1px solid rgba(220, 53, 69, 0.5);
  color: var(--text-light);
  padding: 25px;
  border-radius: var(--border-radius);
  margin: 30px;
  text-align: center;
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  gap: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: ${fadeIn} 0.3s ease-in-out;
  
  svg { 
    font-size: 40px; 
    color: var(--error-color);
    margin-bottom: 10px;
  }
  
  button {
    margin-top: 15px; 
    background-color: rgba(220, 53, 69, 0.6);
    color: white; 
    border: none; 
    padding: 12px 24px;
    border-radius: var(--border-radius); 
    cursor: pointer; 
    transition: all 0.3s;
    font-weight: 500;
    
    &:hover { 
      background-color: rgba(220, 53, 69, 0.8);
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(220, 53, 69, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

const NoEpisodesMessage = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 30px 20px; text-align: center;
  background-color: #0a0a0a; /* Match EpisodeSidebarSection */
  height: 100%; /* Fill available space in EpisodeList */
  svg { font-size: 30px; color: var(--primary-light); margin-bottom: 15px; }
  h4 { font-size: 18px; margin-bottom: 10px; }
  p { color: var(--text-dim); max-width: 300px; }
`;

// Add a styled component for movies in the left panel
const MovieInfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 30px 20px;
  text-align: center;
  background-color: #0a0a0a;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    margin-bottom: 20px;
  }
  
  h3 {
    font-size: 22px;
    margin-bottom: 15px;
  }
  
  p {
    color: var(--text-dim);
    line-height: 1.6;
    max-width: 300px;
    margin-bottom: 20px;
  }
`;

const MovieInfoButton = styled.button`
  background-color: rgba(59, 130, 246, 0.2);
  border: 1px solid var(--primary-light);
  color: var(--primary-light);
  padding: 10px 20px;
  border-radius: var(--border-radius);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-2px);
  }
  
  svg {
    font-size: 16px;
    margin: 0;
  }
`;

//Overlay for Season
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  
  svg {
    color: var(--primary-light);
    font-size: 30px;
    animation: ${spin} 1s linear infinite;
    filter: drop-shadow(0 0 5px rgba(59, 130, 246, 0.5));
  }
`;

// remains globally positioned
const SharePopup = styled.div`
  position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
  background-color: #1a1a1a; border-radius: var(--border-radius);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5); padding: 20px;
  width: 90%; max-width: 500px; z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
  h3 { margin-top: 0; margin-bottom: 15px; display: flex; align-items: center; gap: 10px;
    svg { color: var(--primary-light); }
  }
`;
const ShareUrlContainer = styled.div`
  display: flex; margin-bottom: 15px;
  input { flex: 1; padding: 10px; background-color: #2a2a2a; border: 1px solid #3a3a3a;
    border-radius: var(--border-radius) 0 0 var(--border-radius); color: var(--text-light); font-size: 14px;
  }
  button { background-color: var(--primary-color); color: white; border: none;
    border-radius: 0 var(--border-radius) var(--border-radius) 0; padding: 0 15px; cursor: pointer;
    transition: background-color 0.2s;
    &:hover { background-color: var(--primary-light); }
    svg { font-size: 16px; }
  }
`;
const ShareOptions = styled.div`
  display: flex; justify-content: space-between;
  button { flex: 1; margin: 0 5px; padding: 10px; background-color: #2a2a2a; border: none;
    border-radius: var(--border-radius); color: var(--text-light); cursor: pointer; transition: all 0.2s;
    &:hover { background-color: #3a3a3a; transform: translateY(-2px); }
    &:first-child { margin-left: 0; }
    &:last-child { margin-right: 0; }
  }
`;
const CloseButton = styled.button`
  position: absolute; top: 10px; right: 10px; background: none; border: none;
  color: var(--text-dim); font-size: 18px; cursor: pointer;
  &:hover { color: var(--text-light); }
`;
const ShareOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.7); z-index: 999;
`;
const Toast = styled.div`
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8); color: white; padding: 10px 20px;
  border-radius: 4px; z-index: 1001; animation: ${fadeIn} 0.3s ease-out;
`;

const NavigationLoadingOverlay = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: ${fadeIn} 0.3s ease-in-out;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const NavigationLoadingContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    font-size: 20px;
    color: var(--primary-light);
    animation: ${spin} 1s linear infinite;
  }
`;

const NavigationLoadingText = styled.div`
  font-size: 14px;
  color: white;
`;

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Helper function to check if a source is an unofficial dub and extract dubber info
const extractDubberInfo = (sourceLabel) => {
  if (!sourceLabel) return null;
  
  const dubPattern = /\(d:([^)]+)\)/i;
  const match = sourceLabel.match(dubPattern);
  
  if (match && match[1]) {
    return {
      isDub: true,
      dubber: match[1].trim(),
      cleanLabel: sourceLabel.replace(dubPattern, '').trim()
    };
  }
  
  return null;
};

const PlayerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUserData } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);
  const [playerData, setPlayerData] = useState(null);
  const [videoToken, setVideoToken] = useState('');
  const [currentUrl, setCurrentUrl] = useState('');
  const [useCustomPlayer, setUseCustomPlayer] = useState(false);
  const [videoSources, setVideoSources] = useState([]);
  const [similarContent, setSimilarContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingState, setLoadingState] = useState('initial'); // 'initial', 'loading', 'success', 'error'
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const episodeListRef = useRef(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const [watchedEpisodes, setWatchedEpisodes] = useState(new Set());
  const fullscreenAppliedRef = useRef(false);
  const currentEpisodeRef = useRef(null);
  const scrollAttemptedRef = useRef(false);
  const lastProgressUpdateRef = useRef(Date.now());
  const lastProgressValueRef = useRef(0); // Ref to track the last progress percentage
  const progressUpdateTimerRef = useRef(null);
  const watchStatusUpdateQueue = useRef([]);
  const isUpdatingWatchStatus = useRef(false);
  const showDataCache = useRef(new Map());
  const episodeDataCache = useRef(new Map());
  const preloadedEpisodesRef = useRef(new Map());
  const episodeCache = useRef(new Map());
  
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const shareUrlRef = useRef(null);
  
  const [autoplayNext, setAutoplayNext] = useState(true);
  const [loopPlayback, setLoopPlayback] = useState(false);
  const [isWorkersStream, setIsWorkersStream] = useState(false);
  
  const [episodeList, setEpisodeList] = useState([]);
  const [filteredEpisodes, setFilteredEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);
  const [changingSeasons, setChangingSeasons] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(false);
  
  const lastNavigationRef = useRef({ id: null, timestamp: 0 });
  const loadingTimeoutRef = useRef(null); // loading timeout
  const maxLoadingTime = 30000; // 30 
  const [viewMode, setViewMode] = useState('list'); // list/grid toggle
  
  const [showSourcesMenu, setShowSourcesMenu] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const [hasDescriptionOverflow, setHasDescriptionOverflow] = useState(false);
  
  // Inside the PlayerPage component, add a state for showing/hiding the seasons menu
  const [showSeasonsMenu, setShowSeasonsMenu] = useState(false);
  
  // Add state for report and suggestion modals
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  
  // Add functions to check if any modal is open
  const isAnyModalOpen = useCallback(() => {
    return showReportModal || showSuggestionModal || showSharePopup || showSourcesMenu || showSeasonsMenu;
  }, [showReportModal, showSuggestionModal, showSharePopup, showSourcesMenu, showSeasonsMenu]);
  
  // Modify the keyboard shortcut handler to check for modals
  useEffect(() => {
    const preventPlayerShortcuts = (e) => {
      // If any modal is open, don't let player shortcuts work
      if (isAnyModalOpen() && 
          (e.key === ' ' || e.key === 'k' || e.key === 'f' || 
           e.key === 'm' || e.key === 'ArrowRight' || e.key === 'ArrowLeft' || 
           e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        // Do not prevent default for input fields, but prevent it otherwise
        const isInputActive = document.activeElement.tagName === 'INPUT' || 
                            document.activeElement.tagName === 'TEXTAREA';
        if (!isInputActive) {
          e.stopPropagation();
        }
      }
    };
    
    document.addEventListener('keydown', preventPlayerShortcuts, true);
    return () => {
      document.removeEventListener('keydown', preventPlayerShortcuts, true);
    };
  }, [isAnyModalOpen]);
  
  const toggleViewMode = useCallback(() => {
    setViewMode(prev => prev === 'list' ? 'grid' : 'list');
  }, []);
  
  const checkDescriptionOverflow = useCallback(() => {
    if (descriptionRef.current && window.innerWidth <= 768) {
      const hasOverflow = descriptionRef.current.scrollHeight > 80;
      setHasDescriptionOverflow(hasOverflow);
    } else {
      setHasDescriptionOverflow(false);
    }
  }, []);
  
  useEffect(() => {
    checkDescriptionOverflow();
    const handleResize = debounce(checkDescriptionOverflow, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkDescriptionOverflow, playerData]);
  
  // Toggle sources dropdown menu
  const toggleSourcesMenu = useCallback(() => {
    setShowSourcesMenu(prev => !prev);
  }, []);
  
  // Close sources dropdown when clicking outside
  const handleClickOutside = useCallback((e) => {
    if (showSourcesMenu && !e.target.closest('.source-dropdown')) {
      setShowSourcesMenu(false);
    }
  }, [showSourcesMenu]);
  
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);
  
  useEffect(() => {
    // security implementation
    disableDevtool({
      disableMenu: true,
      disableSelect: false,
      disableCopy: false,
      disableCut: false,
      disablePaste: false,
      clearLog: true,
      disableIdb: false,
      disableFetch: false,
      disableXhr: false,
      disableCache: true,
      disableNotification: true,
      disableEval: true,
      interval: 100,
      ondevtoolopen: (opened) => {
        if (opened) {
          window.location.href = '/';
        }
      },
      disableKeys: [
        'F12',
        'ControlShiftI',
        'ControlShiftJ',
        'ControlShiftC',
        'MetaAltI',
        'MetaAltJ',
        'MetaAltC',
      ],
      features: [
        'debugger',
        'breakpoints',
        'timeline',
        'profiler',
        'console',
      ]
    });
    
    // additional security measures
    
    // override console methods
    const originalConsole = { ...console };
    const methods = ['log', 'info', 'warn', 'error', 'debug', 'table'];
    methods.forEach(method => {
      console[method] = () => {};
    });
    
    // detect devtools via window size
    const detectDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > 160;
      const heightThreshold = window.outerHeight - window.innerHeight > 160;
      if (widthThreshold || heightThreshold) {
        window.location.href = '/';
      }
    };
    
    // detect debugger via timing
    const detectDebugger = () => {
      const start = Date.now();
      debugger;
      const end = Date.now();
      if (end - start > 100) {
        window.location.href = '/';
      }
    };
    
    // prevent right clicking
    const preventRightClick = (e) => {
      e.preventDefault();
      return false;
    };
    
    // block keyboard shortcuts
    const preventShortcuts = (e) => {
      // F12
      if (e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
      
      // Ctrl+Shift+I/J/C
      if (e.ctrlKey && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
        e.preventDefault();
        return false;
      }
      
      // Cmd+Option+I/J/C (Mac)
      if (e.metaKey && e.altKey && (e.keyCode === 73 || e.keyCode === 74 || e.keyCode === 67)) {
        e.preventDefault();
        return false;
      }
    };
    
    // block source viewing
    const preventSourceView = (e) => {
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        return false;
      }
    };
    
    window.addEventListener('resize', detectDevTools);
    document.addEventListener('contextmenu', preventRightClick);
    document.addEventListener('keydown', preventShortcuts);
    document.addEventListener('keydown', preventSourceView);
    
    // run security checks periodically
    const detectionInterval = setInterval(() => {
      detectDevTools();
      try {
        detectDebugger();
      } catch (e) {
        // ignore errors
      }
    }, 1000);
    
    // clean up
    return () => {
      window.removeEventListener('resize', detectDevTools);
      document.removeEventListener('contextmenu', preventRightClick);
      document.removeEventListener('keydown', preventShortcuts);
      document.removeEventListener('keydown', preventSourceView);
      clearInterval(detectionInterval);
      
      // restore console
      Object.keys(originalConsole).forEach(key => {
        console[key] = originalConsole[key];
      });
    };
  }, []);
  
  useEffect(() => {
    const originalBackgroundColor = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#000000';
    return () => {
      document.body.style.backgroundColor = originalBackgroundColor;
    };
  }, []);
  
  const getImageUrl = useCallback((primarySrc, fallbackSrc = '/placeholder.jpg') => {
    if (window.imageCache && window.imageCache[primarySrc] === false) {
      return fallbackSrc;
    }
    return primarySrc || fallbackSrc;
  }, []);
  
  const handleImageError = useCallback((e) => {
    if (window.imageCache && e.target.src !== '/placeholder.jpg') {
      window.imageCache[e.target.src] = false;
      e.target.src = '/placeholder.jpg';
    }
  }, []);
  
  const handleOpenReportModal = useCallback((e) => {
    if (e) e.stopPropagation();
    setShowReportModal(true);
  }, []);
  
  const handleCloseReportModal = useCallback(() => {
    setShowReportModal(false);
  }, []);
  
  const handleOpenSuggestionModal = useCallback((e) => {
    if (e) e.stopPropagation();
    setShowSuggestionModal(true);
  }, []);
  
  const handleCloseSuggestionModal = useCallback(() => {
    setShowSuggestionModal(false);
  }, []);
  
  const isCloudFareLink = useCallback((url) => {
    if (!url) return false;
    return url.includes('v1.m3u8lock.workers.dev/playlist/');
  }, []);
  
  const extractTokenFromUrl = useCallback((url) => {
    if (!url) return null;
    if (url.includes('v1.m3u8lock.workers.dev/playlist/')) {
      return url.split('/playlist/')[1];
    }
    return null;
  }, []);
  
  const processWatchStatusQueue = useCallback(async () => {
    if (isUpdatingWatchStatus.current || watchStatusUpdateQueue.current.length === 0) {
      return;
    }
    isUpdatingWatchStatus.current = true;
    try {
      // Get only the most recent update for each content item
      const uniqueUpdates = new Map();
      for (let i = watchStatusUpdateQueue.current.length - 1; i >= 0; i--) {
        const update = watchStatusUpdateQueue.current[i];
        const key = `${update.type}-${update.id}`;
        if (!uniqueUpdates.has(key)) {
          uniqueUpdates.set(key, update);
        }
      }
      
      // Process updates with a small delay between each to avoid overwhelming the server
      const updatePromises = [];
      let updateCount = 0;
      
      for (const update of uniqueUpdates.values()) {
        // Only process if it's been at least 3 seconds since the last update for this content
        const now = Date.now();
        const lastUpdateTime = update.timestamp || 0;
        const timeSinceLastUpdate = now - lastUpdateTime;
        
        // Skip if we've updated this item recently (unless it's a completion update)
        if (!update.completed && timeSinceLastUpdate < 3000 && updateCount > 0) {
          continue;
        }
        
        updateCount++;
        const updatePromise = (async () => {
          try {
            if (update.type === 'movie') {
              await MoviesAPI.updateWatchStatus(update.id, {
                progress: update.progress, completed: update.completed, userId: update.userId
              });
            } else if (update.type === 'episode') {
              await ShowsAPI.updateEpisodeWatchStatus(update.id, {
                progress: update.progress, completed: update.completed, userId: update.userId
              });
            }
          } catch (err) { /* silently fail individual updates */ }
        })();
        
        updatePromises.push(updatePromise);
        
        // Add a small delay between requests
        if (updatePromises.length > 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Clear the queue
      watchStatusUpdateQueue.current = [];
      
      // Only refresh user data if we actually processed updates
      if (updateCount > 0) {
        refreshUserData();
      }
    } finally {
      isUpdatingWatchStatus.current = false;
    }
  }, [refreshUserData]);
  
  const queueWatchStatusUpdate = useCallback((type, id, progress, completed = false) => {
    if (!id) return;
    
    // Mark episode as watched in local state and storage if completed
    if (completed && type === 'episode') {
      setWatchedEpisodes(prev => new Set([...prev, id]));
      try {
        const watchedEpisodesStorage = JSON.parse(localStorage.getItem('watchedEpisodes') || '[]');
        if (!watchedEpisodesStorage.includes(id)) {
          watchedEpisodesStorage.push(id);
          localStorage.setItem('watchedEpisodes', JSON.stringify(watchedEpisodesStorage));
        }
      } catch (e) { /* silent fail */ }
    }
    
    if (currentUser) {
      // Check if we already have a recent update for this item
      const now = Date.now();
      const existingUpdateIndex = watchStatusUpdateQueue.current.findIndex(
        update => update.type === type && update.id === id
      );
      
      // If we have an existing update less than 10 seconds old, update it instead of adding a new one
      if (existingUpdateIndex !== -1) {
        const existingUpdate = watchStatusUpdateQueue.current[existingUpdateIndex];
        const timeSinceLastUpdate = now - existingUpdate.timestamp;
        
        // If the last update was recent and the progress change is small, just update the existing entry
        if (timeSinceLastUpdate < 10000 && Math.abs(existingUpdate.progress - progress) < 10 && !completed) {
          watchStatusUpdateQueue.current[existingUpdateIndex].progress = progress;
          watchStatusUpdateQueue.current[existingUpdateIndex].timestamp = now;
          return; // Don't add a new entry or process queue
        }
      }
      
      // Add to queue
      watchStatusUpdateQueue.current.push({
        type, id, progress, completed, userId: currentUser._id, timestamp: now
      });
      
      // Process immediately only for important updates:
      // 1. When an item is completed
      // 2. When queue gets too large (10+ items)
      // 3. When progress is at key points (25%, 50%, 75%, 90%, 100%)
      const isKeyProgressPoint = [25, 50, 75, 90, 100].includes(progress);
      
      if (completed || watchStatusUpdateQueue.current.length >= 10 || isKeyProgressPoint) {
        processWatchStatusQueue();
      }
    }
  }, [currentUser, processWatchStatusQueue]);
  
  const fetchEpisodeData = useCallback(async (episodeId, retryCount = 0) => {
    if (episodeDataCache.current.has(episodeId)) return episodeDataCache.current.get(episodeId);
    if (preloadedEpisodesRef.current.has(episodeId)) {
      const episodeData = preloadedEpisodesRef.current.get(episodeId);
      if (episodeData) {
        episodeDataCache.current.set(episodeId, episodeData);
        return episodeData;
      }
    }
    
    const cachedData = apiCache.get('episodes', episodeId);
    if (cachedData) {
      episodeDataCache.current.set(episodeId, cachedData);
      return cachedData;
    }
    
    try {
      const response = await ShowsAPI.getEpisodeById(episodeId);
      if (!response.data.success) throw new Error('Failed to load episode data');
      const episodeData = response.data.data;
      apiCache.set('episodes', episodeId, episodeData);
      episodeDataCache.current.set(episodeId, episodeData);
      return episodeData;
    } catch (err) {
      // Retry up to 2 times with increasing delay
      if (retryCount < 2) {
        console.log(`Retrying episode fetch for ${episodeId}, attempt ${retryCount + 1}`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchEpisodeData(episodeId, retryCount + 1);
      }
      throw err;
    }
  }, []);
  
  const fetchMovieData = useCallback(async (movieId) => {
    const cachedData = apiCache.get('movies', movieId);
    if (cachedData) return cachedData;
    const response = await MoviesAPI.getById(movieId);
    if (!response.data.success) throw new Error('Failed to load movie data');
    const movieData = response.data.data;
    apiCache.set('movies', movieId, movieData);
    return movieData;
  }, []);
  
  // Add a timeout wrapper for API calls
  const fetchWithTimeout = useCallback(async (fetchPromise, timeoutMs = 10000) => {
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out')), timeoutMs);
    });
    
    return Promise.race([fetchPromise, timeoutPromise]);
  }, []);
  
  const fetchAllSeasonEpisodes = useCallback(async (showId, seasonId) => {
    try {
      const cacheKey = `${seasonId}-all`;
      if (episodeCache.current.has(cacheKey)) {
        const cachedData = episodeCache.current.get(cacheKey);
        console.log("Using cached season episodes");
        setEpisodeList(cachedData);
        setFilteredEpisodes(cachedData);
        return cachedData;
      }
      
      setLoadingEpisodes(true);
      
      try {
        console.log(`Fetching all episodes for season ${seasonId}`);
        const response = await fetchWithTimeout(ShowsAPI.getAllSeasonEpisodes(showId, seasonId));
        
        if (!response.data.success) throw new Error('Failed to load episodes');
        
        const episodes = response.data.data;
        console.log(`Received ${episodes.length} episodes for season`);
        
        episodeCache.current.set(cacheKey, episodes);
        setEpisodeList(episodes);
        setFilteredEpisodes(episodes);
        setLoadingEpisodes(false);
        return episodes;
      } catch (error) {
        console.error('Error fetching season episodes, retrying:', error);
        
        // Retry once with increased timeout
        try {
          const retryResponse = await fetchWithTimeout(
            ShowsAPI.getAllSeasonEpisodes(showId, seasonId),
            15000 // Longer timeout for retry
          );
          
          if (!retryResponse.data.success) throw new Error('Failed to load episodes on retry');
          
          const episodes = retryResponse.data.data;
          console.log(`Retry successful, received ${episodes.length} episodes`);
          
          episodeCache.current.set(cacheKey, episodes);
          setEpisodeList(episodes);
          setFilteredEpisodes(episodes);
          setLoadingEpisodes(false);
          return episodes;
        } catch (retryError) {
          console.error('Retry failed:', retryError);
          setLoadingEpisodes(false);
          throw retryError; // Rethrow to be handled by caller
        }
      }
    } catch (error) {
      console.error('Error in fetchAllSeasonEpisodes:', error);
      setLoadingEpisodes(false);
      return [];
    }
  }, [fetchWithTimeout]);
  
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredEpisodes(episodeList);
      return;
    }
    const lowercaseQuery = query.toLowerCase();
    const filtered = episodeList.filter(episode => 
      episode.title.toLowerCase().includes(lowercaseQuery) ||
      episode.episodeNumber.toString().includes(lowercaseQuery)
    );
    setFilteredEpisodes(filtered);
  }, [episodeList]);
  
  useEffect(() => {
    if (searchQuery) handleSearch(searchQuery);
    else setFilteredEpisodes(episodeList);
  }, [episodeList, searchQuery, handleSearch]);
  
  const preloadAdjacentEpisodes = useCallback(async (currentEpisode, season) => {
    if (!currentEpisode || !season) return;
    
    const currentEpisodeNumber = currentEpisode.episodeNumber;
    const episodesToPreload = [];
    
    // Preload next 2 episodes and previous episode for better navigation
    const prevEpisode = episodeList.find(ep => ep.episodeNumber === currentEpisodeNumber - 1);
    const nextEpisode = episodeList.find(ep => ep.episodeNumber === currentEpisodeNumber + 1);
    const nextNextEpisode = episodeList.find(ep => ep.episodeNumber === currentEpisodeNumber + 2);
    
    // Add episodes to preload queue if they exist and aren't already cached
    if (prevEpisode && !episodeDataCache.current.has(prevEpisode._id) && !preloadedEpisodesRef.current.has(prevEpisode._id)) {
      episodesToPreload.push(prevEpisode._id);
    }
    
    // Prioritize next episodes for autoplay
    if (nextEpisode && !episodeDataCache.current.has(nextEpisode._id) && !preloadedEpisodesRef.current.has(nextEpisode._id)) {
      episodesToPreload.push(nextEpisode._id);
    }
    
    if (nextNextEpisode && !episodeDataCache.current.has(nextNextEpisode._id) && !preloadedEpisodesRef.current.has(nextNextEpisode._id)) {
      episodesToPreload.push(nextNextEpisode._id);
    }
    
    // Use Promise.all for parallel fetching but with a small delay between requests to avoid rate limiting
    const preloadPromises = episodesToPreload.map((episodeId, index) => {
      return new Promise(resolve => {
        setTimeout(async () => {
          try {
            preloadedEpisodesRef.current.set(episodeId, null); // Mark as being preloaded
            const response = await ShowsAPI.getEpisodeById(episodeId);
            if (response.data.success) {
              preloadedEpisodesRef.current.set(episodeId, response.data.data);
              console.log(`Preloaded episode ${episodeId}`);
            }
          } catch (err) {
            console.error(`Failed to preload episode ${episodeId}:`, err);
            preloadedEpisodesRef.current.delete(episodeId);
          }
          resolve();
        }, index * 300); // 300ms delay between requests
      });
    });
    
    await Promise.all(preloadPromises);
  }, [episodeList]);
  
  const preloadAdjacentSeasons = useCallback(async (show, currentSeasonNumber) => {
    if (!show || !show.seasons || !Array.isArray(show.seasons)) return;
    const prevSeasonNumber = currentSeasonNumber - 1;
    const nextSeasonNumber = currentSeasonNumber + 1;
    if (prevSeasonNumber > 0 && !showDataCache.current.has(`${show._id}-${prevSeasonNumber}`)) {
      const prevSeason = show.seasons.find(s => s.seasonNumber === prevSeasonNumber);
      if (prevSeason) showDataCache.current.set(`${show._id}-${prevSeasonNumber}`, prevSeason);
    }
    const nextSeason = show.seasons.find(s => s.seasonNumber === nextSeasonNumber);
    if (nextSeason && !showDataCache.current.has(`${show._id}-${nextSeasonNumber}`)) {
      showDataCache.current.set(`${show._id}-${nextSeasonNumber}`, nextSeason);
    }
  }, []);
  
  const startProgressTracking = useCallback((data) => {
    if (!data || !data.id) return;
    if (progressUpdateTimerRef.current) clearInterval(progressUpdateTimerRef.current);
    
    // Set interval to 60 seconds for background updates
    progressUpdateTimerRef.current = setInterval(() => {
      try {
        if (useCustomPlayer && playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          if (currentTime && duration) {
            const progress = Math.floor((currentTime / duration) * 100);
            // Only update if significant progress has been made since last update
            if (Math.abs(progress - lastProgressValueRef.current) > 5) {
              queueWatchStatusUpdate(data.type, data.id, progress, progress >= 90);
              lastProgressValueRef.current = progress;
            }
          } else {
            // Initial progress update
            if (lastProgressValueRef.current === 0) {
              queueWatchStatusUpdate(data.type, data.id, 5, false);
              lastProgressValueRef.current = 5;
            }
          }
        } else if (lastProgressValueRef.current === 0) {
          // For non-custom player, just send an initial progress
          queueWatchStatusUpdate(data.type, data.id, 5, false);
          lastProgressValueRef.current = 5;
        }
      } catch (err) { /* silenced */ }
    }, 60000); // Increased from 30s to 60s
    
    return () => { if (progressUpdateTimerRef.current) clearInterval(progressUpdateTimerRef.current); };
  }, [queueWatchStatusUpdate, useCustomPlayer]);
  
  const loadSimilarContent = useCallback(async (data) => {
    if (!data || (!data.id && !data.showId)) return;
    try {
      if (similarContent.length > 0) return;
      if (data.type === 'movie' && data.id) {
        const movieData = await fetchMovieData(data.id);
        if (movieData && movieData.related) setSimilarContent(movieData.related.slice(0, 6) || []);
      } else if (data.showId) {
        const cachedShow = apiCache.get('shows', data.showId);
        if (cachedShow && cachedShow.related) {
          setSimilarContent(cachedShow.related.slice(0, 6) || []); return;
        }
        const response = await ShowsAPI.getById(data.showId);
        if (response?.data?.data?.related) {
          apiCache.set('shows', data.showId, response.data.data);
          setSimilarContent(response.data.data.related.slice(0, 6) || []);
        }
      }
    } catch (err) { /* Dont block main content */ }
  }, [similarContent.length, fetchMovieData]);
  
  const loadEpisodeFromUrl = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const episodeId = urlParams.get('episodeId');
    const movieId = urlParams.get('movieId');
    const listParam = urlParams.get('list');
    
    // Set loading state immediately to prevent multiple requests
    setLoading(true);
    setLoadingState('loading');
    setError(null); // Clear any previous errors
    
    // Use the existing lastNavigationRef instead of creating a new ref
    const loadKey = episodeId || movieId || listParam;
    
    // If we've already loaded this URL, don't load it again
    if (lastNavigationRef.current.id === loadKey && Date.now() - lastNavigationRef.current.timestamp < 2000) {
      console.log("Preventing duplicate URL load");
      if (playerData) {
        setLoading(false);
        setLoadingState('success');
      }
      return true;
    }
    
    // Update the last navigation reference
    lastNavigationRef.current = { id: loadKey, timestamp: Date.now() };
    
    // If no valid parameters, show error after a delay
    if (!episodeId && !movieId && !listParam) {
      console.log("No valid URL parameters found");
      
      // Set a timeout to show the error message
      setTimeout(() => {
        setLoading(false);
        setLoadingState('error');
        setError('No video data provided.');
      }, 500); // Small delay to prevent flashing
      
      return false;
    }
    
    if (episodeId) {
      let retryCount = 0;
      const maxRetries = 3;
      let success = false;
      
      while (retryCount < maxRetries && !success) {
        try {
          console.log(`Loading episode from URL: ${episodeId} (Attempt ${retryCount + 1})`);
          
          // First check if we have this episode in cache
          let episodeData;
          if (episodeDataCache.current.has(episodeId)) {
            console.log("Using cached episode data from URL load");
            episodeData = episodeDataCache.current.get(episodeId);
          } else {
            // Try to fetch with timeout to avoid hanging
            try {
              const episodeResponse = await fetchWithTimeout(
                ShowsAPI.getEpisodeById(episodeId),
                15000 + (retryCount * 5000) // Increase timeout with each retry
              );
              
              if (!episodeResponse?.data?.success) {
                throw new Error('Failed to load episode data: API returned unsuccessful response');
              }
              
              episodeData = episodeResponse.data.data;
              if (!episodeData) {
                throw new Error('Failed to load episode data: API returned empty data');
              }
              
              episodeDataCache.current.set(episodeId, episodeData);
            } catch (err) {
              console.error(`Error fetching episode data (attempt ${retryCount + 1}):`, err);
              retryCount++;
              
              if (retryCount >= maxRetries) {
                setError('Failed to load episode data after multiple attempts. Please try again.');
                setLoading(false);
                setLoadingState('error');
                return false;
              }
              
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
              continue; // Skip to next retry
            }
          }
          
          // Verify episode data structure
          const show = episodeData?.seasonId?.showId;
          const season = episodeData?.seasonId;
          
          if (!show || !season) {
            console.error('Invalid episode data structure:', { hasShow: !!show, hasSeason: !!season, episodeData });
            retryCount++;
            
            if (retryCount >= maxRetries) {
              setError('Invalid episode data: missing show or season information');
              setLoading(false);
              setLoadingState('error');
              return false;
            }
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue; // Skip to next retry
          }
          
          const newState = { 
            id: episodeId, 
            showId: show?._id, 
            type: 'episode',
            title: `${show?.title || 'Unknown Show'}`,
            episodeTitle: episodeData.title || `Episode ${episodeData.episodeNumber}`,
            sources: episodeData.links?.map(link => ({ label: link.name, url: link.url })) || [{ label: 'Default', url: '' }],
            description: episodeData.description, 
            show, 
            season, 
            episode: episodeData, 
            autoplay: true,
            seasonNumber: season?.seasonNumber || 1,
            episodeNumber: episodeData.episodeNumber || 1
          };
          
          // Set player data before loading episodes to prevent "no data" flash
          setPlayerData(newState);
          
          if (newState.sources?.length > 0) {
            setVideoSources(newState.sources);
            const sourceUrl = newState.sources[0].url;
            if (isCloudFareLink(sourceUrl)) {
              setVideoToken(extractTokenFromUrl(sourceUrl) || ''); 
              setUseCustomPlayer(true);
            } else {
              setCurrentUrl(sourceUrl); 
              setUseCustomPlayer(false);
            }
            setCurrentSourceIndex(0);
          }
          
          startProgressTracking(newState);
          
          // Load episodes for the season - but only once
          if (season && show) {
            try {
              const cacheKey = `${season._id}-all`;
              if (!episodeCache.current.has(cacheKey)) {
                console.log(`Loading season episodes for direct URL: ${show._id}, ${season._id}`);
                await fetchAllSeasonEpisodes(show._id, season._id);
              } else {
                console.log("Using cached season episodes for direct URL");
                const cachedEpisodes = episodeCache.current.get(cacheKey);
                setEpisodeList(cachedEpisodes);
                setFilteredEpisodes(cachedEpisodes);
              }
            } catch (err) {
              console.error("Error loading season episodes:", err);
              // Continue even if this fails
            }
          }
          
          // load similar content while user watches
          loadSimilarContent(newState).catch(err => {
            console.error("Error loading similar content:", err);
            // continue even if this fails
          });
          
          // preload episodes for smoother navigation
          if (season) {
            setTimeout(() => {
              preloadAdjacentEpisodes(episodeData, season).catch(err => {
                console.error("Error preloading adjacent episodes:", err);
                // continue even if this fails
              });
            }, 2000);
          }
          
          // preload seasons for smoother navigation
          if (show?.seasons) {
            setTimeout(() => {
              preloadAdjacentSeasons(show, season.seasonNumber).catch(err => {
                console.error("Error preloading adjacent seasons:", err);
                // continue even if this fails
              });
            }, 3000);
          }
          
          // finish loading after all important data is ready
          setLoading(false);
          setLoadingState('success');
          success = true;
          return true;
        } catch (err) {
          console.error(`Error in loadEpisodeFromUrl (episode) attempt ${retryCount + 1}:`, err);
          retryCount++;
          
          if (retryCount >= maxRetries) {
            setError('Failed to load episode data. Please try again.');
            setLoading(false);
            setLoadingState('error');
            return false;
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }
    } else if (movieId) {
      let retryCount = 0;
      const maxRetries = 3;
      let success = false;
      
      while (retryCount < maxRetries && !success) {
        try {
          console.log(`Loading movie from URL: ${movieId} (Attempt ${retryCount + 1})`);
          
          let movieData;
          try {
            const movieResponse = await fetchWithTimeout(
              MoviesAPI.getById(movieId),
              15000 + (retryCount * 5000) // Increase timeout with each retry
            );
            
            if (!movieResponse?.data?.success) {
              throw new Error('Failed to load movie data: API returned unsuccessful response');
            }
            
            movieData = movieResponse.data.data;
            if (!movieData) {
              throw new Error('Failed to load movie data: API returned empty data');
            }
          } catch (err) {
            console.error(`Error fetching movie data (attempt ${retryCount + 1}):`, err);
            retryCount++;
            
            if (retryCount >= maxRetries) {
              setError('Failed to load movie data after multiple attempts. Please try again.');
              setLoading(false);
              setLoadingState('error');
              return false;
            }
            
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue; // Skip to next retry
          }
          
          const newState = { 
            id: movieId, 
            type: 'movie', 
            title: movieData.title,
            sources: movieData.links?.map(link => ({ label: link.name, url: link.url })) || [{ label: 'Default', url: '' }],
            description: movieData.description, 
            autoplay: true
          };
          
          // Set player data before other operations to prevent "no data" flash
          setPlayerData(newState);
          
          if (newState.sources?.length > 0) {
            setVideoSources(newState.sources);
            const sourceUrl = newState.sources[0].url;
            if (isCloudFareLink(sourceUrl)) {
              setVideoToken(extractTokenFromUrl(sourceUrl) || ''); 
              setUseCustomPlayer(true);
            } else {
              setCurrentUrl(sourceUrl); 
              setUseCustomPlayer(false);
            }
            setCurrentSourceIndex(0);
          }
          
          startProgressTracking(newState);
          
          // load similar content while user watches
          loadSimilarContent(newState).catch(err => {
            console.error("Error loading similar content:", err);
            // continue even if this fails
          });
          
          setLoading(false);
          setLoadingState('success');
          success = true;
          return true;
        } catch (err) {
          console.error(`Error in loadEpisodeFromUrl (movie) attempt ${retryCount + 1}:`, err);
          retryCount++;
          
          if (retryCount >= maxRetries) {
            setError('Failed to load movie data. Please try again.');
            setLoading(false);
            setLoadingState('error');
            return false;
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }
    } else if (listParam) {
      const newState = {
        type: 'video', 
        title: 'Video Player', 
        sources: [{ label: 'Default', url: listParam }],
        description: 'Video loaded from URL', 
        autoplay: true
      };
      
      setPlayerData(newState); 
      setVideoSources(newState.sources);
      
      if (isCloudFareLink(listParam)) {
        setVideoToken(extractTokenFromUrl(listParam) || ''); 
        setUseCustomPlayer(true);
      } else {
        setCurrentUrl(listParam); 
        setUseCustomPlayer(false);
      }
      
      setLoading(false);
      setLoadingState('success');
      return true;
    }
    
    setLoading(false);
    setLoadingState('error');
    return false;
  }, [fetchAllSeasonEpisodes, fetchWithTimeout, extractTokenFromUrl, isCloudFareLink, loadSimilarContent, preloadAdjacentEpisodes, preloadAdjacentSeasons, startProgressTracking]);
  
  const scrollToCurrentEpisode = useCallback(() => {
    if (!episodeListRef.current || !playerData?.episode) return false;
    const episodeId = playerData.episode._id;
    const episodeNumber = playerData.episode.episodeNumber;
    let currentEpisodeElement = document.getElementById(`episode-${episodeId}`);
    if (!currentEpisodeElement) {
      const episodeElements = episodeListRef.current.querySelectorAll('.episode-item');
      for (let element of episodeElements) {
        const numberElement = element.querySelector('.episode-number');
        if (numberElement?.textContent.trim() === String(episodeNumber)) {
          currentEpisodeElement = element; break;
        }
      }
    }
    if (currentEpisodeElement) {
      episodeListRef.current.scrollTop = currentEpisodeElement.offsetTop - 100; return true;
    }
    return false;
  }, [playerData]);

  const forceScrollToEpisode = useCallback((episodeNumber) => {
    if (!episodeListRef.current) return;
    const episodeItems = episodeListRef.current.querySelectorAll('.episode-item');
    let targetElement = null;
    for (let i = 0; i < episodeItems.length; i++) {
      const numberElement = episodeItems[i].querySelector('.episode-number');
      if (numberElement?.textContent.trim() === episodeNumber.toString()) {
        targetElement = episodeItems[i]; break;
      }
    }
    if (targetElement) {
      const containerTop = episodeListRef.current.getBoundingClientRect().top;
      const elementTop = targetElement.getBoundingClientRect().top;
      const offset = elementTop - containerTop - 50;
      episodeListRef.current.scrollBy({ top: offset, behavior: 'smooth' });
      targetElement.style.transition = 'background-color 0.3s';
      targetElement.style.backgroundColor = 'rgba(59, 130, 246, 0.3)';
      setTimeout(() => { targetElement.style.backgroundColor = ''; }, 1000);
    }
  }, []);
  
  const handleSourceChange = useCallback((sourceUrl, index) => {
    const isWorkers = isCloudFareLink(sourceUrl);
    setIsWorkersStream(isWorkers);
    
    if (isCloudFareLink(sourceUrl)) {
      setVideoToken(extractTokenFromUrl(sourceUrl) || ''); 
      setUseCustomPlayer(true);
    } else {
      setCurrentUrl(sourceUrl); 
      setUseCustomPlayer(false);
    }
    setCurrentSourceIndex(index);
    setShowSourcesMenu(false); // Close dropdown after selection
  }, [isCloudFareLink, extractTokenFromUrl]);
  
  const handleBackClick = useCallback(() => {
    if (playerData) queueWatchStatusUpdate(playerData.type, playerData.id, 10, false);
    if (playerData) {
      if (playerData.type === 'movie') navigate(`/movie/${playerData.id}`);
      else if (playerData.showId) navigate(`/show/${playerData.showId}`);
      else navigate('/');
    } else navigate('/');
  }, [playerData, navigate, queueWatchStatusUpdate]);
  
  const handleShareClick = useCallback(() => setShowSharePopup(true), []);
  
  const handleCopyLink = useCallback(() => {
    if (shareUrlRef.current) {
      shareUrlRef.current.select(); document.execCommand('copy');
      setShowCopyToast(true); setTimeout(() => setShowCopyToast(false), 2000);
      setShowSharePopup(false);
    }
  }, []);
  
  const getShareUrl = useCallback(() => {
    if (!playerData) return window.location.href;
    const baseUrl = window.location.origin;
    if (playerData.type === 'movie' && playerData.id) return `${baseUrl}/player?movieId=${playerData.id}`;
    if (playerData.type === 'episode' && playerData.id) return `${baseUrl}/player?episodeId=${playerData.id}`;
    return window.location.href;
  }, [playerData]);
  
  const handleShareVia = useCallback((platform) => {
    const shareUrl = getShareUrl();
    const shareTitle = playerData ? playerData.title : 'Check out this video';
    let shareLink = '';
    switch(platform) {
      case 'twitter': shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`; break;
      case 'facebook': shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`; break;
      case 'whatsapp': shareLink = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`; break;
      default: return;
    }
    window.open(shareLink, '_blank'); setShowSharePopup(false);
  }, [getShareUrl, playerData]);
  
  const isEpisodeWatched = useCallback((episodeId) => {
    if (playerData?.episode?._id === episodeId) return true;
    return watchedEpisodes.has(episodeId);
  }, [playerData, watchedEpisodes]);
  
  const handleEpisodeClick = useCallback(async (episode, autoplay = false) => {
    if (!playerData?.show || !playerData.season) return;
    try {
        setError(null); // Clear any previous errors
        const wasFullscreen = isFullscreen; scrollAttemptedRef.current = false;
        if (!watchedEpisodes.has(episode._id) && episode._id) {
            setWatchedEpisodes(prev => new Set([...prev, episode._id]));
            queueWatchStatusUpdate('episode', episode._id, 10, false);
        }
        currentEpisodeRef.current = episode;
        if (episodeListRef.current) localStorage.setItem('episodeListHeight', episodeListRef.current.clientHeight.toString());
        
        let fullEpisodeData;
        if (episodeDataCache.current.has(episode._id)) fullEpisodeData = episodeDataCache.current.get(episode._id);
        else if (preloadedEpisodesRef.current.has(episode._id)) {
            fullEpisodeData = preloadedEpisodesRef.current.get(episode._id);
            if (fullEpisodeData) episodeDataCache.current.set(episode._id, fullEpisodeData);
        } else {
            const episodeResponse = await ShowsAPI.getEpisodeById(episode._id);
            fullEpisodeData = episodeResponse.data.data;
            episodeDataCache.current.set(episode._id, fullEpisodeData);
        }
        if (!fullEpisodeData?.links?.length) { 
            setError('No video sources available for this episode.'); 
            // Auto-dismiss error after 5 seconds
            setTimeout(() => setError(null), 5000);
            return; 
        }
        
        const newState = { 
            id: episode._id, showId: playerData.show._id, type: 'episode',
            title: `${playerData.show.title}`,
            episodeTitle: episode.title || `Episode ${episode.episodeNumber}`,
            sources: fullEpisodeData.links.map(link => ({ label: link.name, url: link.url })),
            description: episode.description || fullEpisodeData.description,
            show: playerData.show, season: playerData.season, episode, wasFullscreen, autoplay: autoplay || true,
            timestamp: Date.now(),
            seasonNumber: playerData.season.seasonNumber || 1,
            episodeNumber: episode.episodeNumber || 1
        };
        setPlayerData(newState);
        localStorage.setItem('scrollToEpisode', episode.episodeNumber.toString());
        if (newState.sources?.length > 0) {
            setVideoSources(newState.sources);
            const sourceUrl = newState.sources[0].url;
            if (isCloudFareLink(sourceUrl)) {
                setVideoToken(extractTokenFromUrl(sourceUrl) || ''); setUseCustomPlayer(true);
            } else {
                setCurrentUrl(sourceUrl); setUseCustomPlayer(false);
            }
            setCurrentSourceIndex(0);
        }
        preloadAdjacentEpisodes(episode, playerData.season);
        navigate('/player', { state: newState, replace: true });
    } catch (err) { 
        console.error("Error in handleEpisodeClick:", err);
        setError('Failed to switch episode. Please try again.'); 
        // Auto-dismiss error after 5 seconds
        setTimeout(() => setError(null), 5000);
    }
}, [playerData, isFullscreen, watchedEpisodes, navigate, queueWatchStatusUpdate, preloadAdjacentEpisodes, extractTokenFromUrl, isCloudFareLink]);
  
  const hasPrevEpisode = useCallback(() => {
    if (!playerData?.show || !playerData?.season || !playerData?.episode) return false;
    const { episodeNumber } = playerData.episode; 
    const { seasonNumber } = playerData.season;
    
    // Check if there's any episode with a lower episode number in the current season
    const hasPrevInSeason = episodeList.some(ep => ep.episodeNumber < episodeNumber);
    
    if (hasPrevInSeason) return true;
    
    // If no previous episode in this season, check for any available previous season
    // Find all available seasons from show data
    const availableSeasons = playerData.show.seasons?.map(s => s.seasonNumber) || [];
    
    // Find if there are any seasons with lower number than current
    const lowerSeasons = availableSeasons.filter(sNum => sNum < seasonNumber);
    
    // Return true if there are any lower seasons or if loop is enabled
    return lowerSeasons.length > 0 || loopPlayback;
  }, [playerData, episodeList, loopPlayback]);
  
  const hasNextEpisode = useCallback(() => {
    if (!playerData?.show || !playerData?.season || !playerData?.episode) return false;
    const { episodeNumber } = playerData.episode; 
    const { seasonNumber } = playerData.season;
    
    // Check if there's any episode with a higher episode number in the current season
    const hasNextInSeason = episodeList.some(ep => ep.episodeNumber > episodeNumber);
    
    if (hasNextInSeason) return true;
    
    // Find all available seasons from show data
    const availableSeasons = playerData.show.seasons?.map(s => s.seasonNumber) || [];
    
    // Find if there are any seasons with higher number than current
    const higherSeasons = availableSeasons.filter(sNum => sNum > seasonNumber);
    
    // Return true if there are any higher seasons or if loop is enabled
    return higherSeasons.length > 0 || loopPlayback;
  }, [playerData, episodeList, loopPlayback]);

  // Modify the navigateToEpisode function to prevent duplicate calls
  const navigateToEpisode = useCallback(async (targetEpisode, targetSeason, wasFullscreen, autoplay = true) => {
    try {
        // Prevent duplicate navigations within 1 second
        const now = Date.now();
        if (
            targetEpisode._id === lastNavigationRef.current.id && 
            now - lastNavigationRef.current.timestamp < 1000
        ) {
            console.log("Preventing duplicate navigation to same episode");
            return;
        }
        
        // Update the last navigation reference
        lastNavigationRef.current = { id: targetEpisode._id, timestamp: now };
        
        setError(null); // Clear any previous errors
        
        // Show navigation loading state
        setNavigationLoading(true);
        
        let fullEpisodeData;
        try {
            if (episodeDataCache.current.has(targetEpisode._id)) {
                fullEpisodeData = episodeDataCache.current.get(targetEpisode._id);
                console.log("Using cached episode data");
            } else if (preloadedEpisodesRef.current.has(targetEpisode._id)) {
                fullEpisodeData = preloadedEpisodesRef.current.get(targetEpisode._id);
                if (fullEpisodeData) {
                    console.log("Using preloaded episode data");
                    episodeDataCache.current.set(targetEpisode._id, fullEpisodeData);
                } else {
                    // If preloaded data is null (still loading), fetch directly
                    console.log("Preloaded data not ready, fetching directly");
                    const episodeResponse = await fetchWithTimeout(ShowsAPI.getEpisodeById(targetEpisode._id));
                    fullEpisodeData = episodeResponse.data.data;
                    episodeDataCache.current.set(targetEpisode._id, fullEpisodeData);
                }
            } else {
                console.log("Fetching episode data directly");
                const episodeResponse = await fetchWithTimeout(ShowsAPI.getEpisodeById(targetEpisode._id));
                fullEpisodeData = episodeResponse.data.data;
                episodeDataCache.current.set(targetEpisode._id, fullEpisodeData);
            }
        } catch (err) {
            console.error("Error fetching episode data:", err);
            // Try one more time with direct API call
            console.log("Retrying episode fetch directly");
            try {
                const episodeResponse = await fetchWithTimeout(
                  ShowsAPI.getEpisodeById(targetEpisode._id),
                  15000 // Longer timeout for retry
                );
                fullEpisodeData = episodeResponse.data.data;
                episodeDataCache.current.set(targetEpisode._id, fullEpisodeData);
            } catch (retryErr) {
                console.error("Retry failed:", retryErr);
                setNavigationLoading(false);
                const errorMsg = 'Failed to load episode data. Please try again.';
                setError(errorMsg);
                setTimeout(() => setError(null), 5000);
                return;
            }
        }

        if (!fullEpisodeData?.links?.length) {
            setNavigationLoading(false);
            const errorMsg = 'No video sources available for this episode.';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
            return;
        }

        const newState = {
            id: targetEpisode._id, 
            showId: playerData.show._id, 
            type: 'episode',
            title: `${playerData.show.title}`,
            episodeTitle: targetEpisode.title || `Episode ${targetEpisode.episodeNumber}`,
            sources: fullEpisodeData.links.map(link => ({ label: link.name, url: link.url })),
            description: targetEpisode.description || fullEpisodeData.description,
            show: playerData.show, 
            season: targetSeason, 
            episode: targetEpisode,
            wasFullscreen, 
            autoplay, 
            timestamp: Date.now(),
            seasonNumber: targetSeason.seasonNumber || 1,
            episodeNumber: targetEpisode.episodeNumber || 1
        };

        // Update state and navigate
        setPlayerData(newState);
        if (newState.sources?.length > 0) {
            setVideoSources(newState.sources);
            const sourceUrl = newState.sources[0].url;
            if (isCloudFareLink(sourceUrl)) {
                setVideoToken(extractTokenFromUrl(sourceUrl) || ''); 
                setUseCustomPlayer(true);
            } else {
                setCurrentUrl(sourceUrl); 
                setUseCustomPlayer(false);
            }
            setCurrentSourceIndex(0);
        }
        
        // Mark as watched in UI immediately
        if (targetEpisode._id) {
            setWatchedEpisodes(prev => new Set([...prev, targetEpisode._id]));
        }
        
        // Navigate to player with new state
        navigate('/player', { state: newState, replace: true });
        
        // Preload adjacent episodes for smoother navigation
        preloadAdjacentEpisodes(targetEpisode, targetSeason);
        
        // Turn off loading state
        setNavigationLoading(false);
        
    } catch (err) {
        console.error("Error in navigateToEpisode:", err);
        setNavigationLoading(false);
        setError("Failed to load episode. Please try again.");
        setTimeout(() => setError(null), 5000);
    }
}, [playerData, navigate, isCloudFareLink, extractTokenFromUrl, preloadAdjacentEpisodes, fetchWithTimeout]);
  
  const handlePrevEpisode = useCallback(async () => {
    if (!playerData?.show || !playerData.season || !playerData.episode) return;
    const currentEpisodeNumber = playerData.episode.episodeNumber;
    const currentSeason = playerData.season;
    const currentSeasonNumber = currentSeason.seasonNumber;

    try {
        setError(null); // Clear any previous errors

        if (currentEpisodeNumber > 1) {
            // Try to find the exact previous episode first
            let prevEpisode = episodeList.find(ep => ep.episodeNumber === currentEpisodeNumber - 1);
            
            // If exact previous episode is not found, find the closest available episode with lower number
            if (!prevEpisode) {
                const lowerEpisodes = episodeList.filter(ep => ep.episodeNumber < currentEpisodeNumber);
                if (lowerEpisodes.length > 0) {
                    // Sort by episode number in descending order to find the closest previous episode
                    lowerEpisodes.sort((a, b) => b.episodeNumber - a.episodeNumber);
                    prevEpisode = lowerEpisodes[0];
                    console.log(`Previous sequential episode not found. Using episode ${prevEpisode.episodeNumber} instead.`);
                }
            }
            
            if (prevEpisode) return navigateToEpisode(prevEpisode, currentSeason, isFullscreen);
        }
        
        // First episode of current season or no previous episodes found
        // Find all available seasons and sort them
        const availableSeasons = [...playerData.show.seasons].sort((a, b) => a.seasonNumber - b.seasonNumber);
        
        // Find the closest lower season number
        let prevSeason = null;
        for (let i = availableSeasons.length - 1; i >= 0; i--) {
          if (availableSeasons[i].seasonNumber < currentSeasonNumber) {
            prevSeason = availableSeasons[i];
            break;
          }
        }
        
        if (prevSeason) {
          setChangingSeasons(true);
          try {
            const episodes = await fetchAllSeasonEpisodes(playerData.show._id, prevSeason._id);
            setChangingSeasons(false);
            if (episodes?.length > 0) return navigateToEpisode(episodes[episodes.length - 1], prevSeason, isFullscreen);
          } catch (err) {
            setChangingSeasons(false);
            console.error("Error loading previous season episodes:", err);
            throw err;
          }
        } else if (loopPlayback) { // First episode of first season with loop enabled
          setChangingSeasons(true);
          try {
            // Get the last available season when looping
            const lastSeason = availableSeasons[availableSeasons.length - 1];
            if (lastSeason) {
              const episodes = await fetchAllSeasonEpisodes(playerData.show._id, lastSeason._id);
              setChangingSeasons(false);
              if (episodes?.length > 0) return navigateToEpisode(episodes[episodes.length - 1], lastSeason, isFullscreen);
            }
          } catch (err) {
            setChangingSeasons(false);
            console.error("Error loading last season episodes for loop:", err);
            throw err;
          }
        }
        setChangingSeasons(false);
    } catch (err) {
        console.error("Error in handlePrevEpisode:", err);
        setError("Failed to load previous episode. Please try again.");
        // Auto-dismiss error after 5 seconds
        setTimeout(() => setError(null), 5000);
        setChangingSeasons(false);
    }
}, [playerData, episodeList, loopPlayback, isFullscreen, navigateToEpisode, fetchAllSeasonEpisodes]);
  
  const hasPrevSeason = useCallback(() => {
    if (!playerData?.show?.seasons) return false;
    const currentSeasonNumber = playerData.season?.seasonNumber;
    return playerData.show.seasons.some(s => s.seasonNumber < currentSeasonNumber);
  }, [playerData]);
  
  const handleNextEpisode = useCallback(async () => {
    if (!playerData?.show || !playerData.season || !playerData.episode) {
      console.error("Cannot navigate to next episode: Missing show, season, or episode data");
      setError("Missing data needed for navigation. Please try again.");
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    const currentEpisodeNumber = playerData.episode.episodeNumber;
    const currentSeason = playerData.season;
    const currentSeasonNumber = currentSeason.seasonNumber;

    try {
        setError(null); // Clear any previous errors
        setNavigationLoading(true);
        
        // Verify we have episode list data
        if (!episodeList || episodeList.length === 0) {
          console.log("Episode list is empty, trying to load episodes");
          try {
            await fetchAllSeasonEpisodes(playerData.show._id, currentSeason._id);
          } catch (loadErr) {
            console.error("Failed to load episode list:", loadErr);
            setNavigationLoading(false);
            setError("Could not load episode data. Please try again.");
            setTimeout(() => setError(null), 5000);
            return;
          }
        }
        
        // Find next episode in current season
        let nextEpisodeInSeason = episodeList.find(ep => ep.episodeNumber === currentEpisodeNumber + 1);
        
        // If exact next episode is not found, find the closest available episode with higher number
        if (!nextEpisodeInSeason) {
          const higherEpisodes = episodeList.filter(ep => ep.episodeNumber > currentEpisodeNumber);
          if (higherEpisodes.length > 0) {
            // Sort by episode number to find the closest next episode
            higherEpisodes.sort((a, b) => a.episodeNumber - b.episodeNumber);
            nextEpisodeInSeason = higherEpisodes[0];
            console.log(`Next sequential episode not found. Using episode ${nextEpisodeInSeason.episodeNumber} instead.`);
          }
        }
        
        if (nextEpisodeInSeason) {
          console.log("Navigating to next episode in current season:", nextEpisodeInSeason.episodeNumber);
          const result = await navigateToEpisode(nextEpisodeInSeason, currentSeason, isFullscreen);
          setNavigationLoading(false);
          return result;
        } else { 
          // Last episode of current season, try next season
          // Verify show data includes seasons
          if (!playerData.show.seasons || !Array.isArray(playerData.show.seasons)) {
            console.error("Show data missing seasons array");
            setNavigationLoading(false);
            setError("Could not load next episode: Missing season data");
            setTimeout(() => setError(null), 5000);
            return;
          }
          
          // Find all available seasons and sort them
          const availableSeasons = [...playerData.show.seasons].sort((a, b) => a.seasonNumber - b.seasonNumber);
          
          // Find the closest higher season number
          let nextSeason = null;
          for (let i = 0; i < availableSeasons.length; i++) {
            if (availableSeasons[i].seasonNumber > currentSeasonNumber) {
              nextSeason = availableSeasons[i];
              break;
            }
          }
          
          if (nextSeason) {
            console.log("Moving to next season:", nextSeason.seasonNumber);
            setChangingSeasons(true);
            try {
              const nextSeasonEpisodes = await fetchAllSeasonEpisodes(playerData.show._id, nextSeason._id);
              setChangingSeasons(false);
              setNavigationLoading(false);
              if (nextSeasonEpisodes?.length > 0) {
                console.log("Navigating to first episode of next season");
                return navigateToEpisode(nextSeasonEpisodes[0], nextSeason, isFullscreen);
              } else {
                throw new Error("No episodes found in next season");
              }
            } catch (seasonErr) {
              console.error("Error loading next season:", seasonErr);
              setChangingSeasons(false);
              setNavigationLoading(false);
              setError("Failed to load next season. Please try again.");
              setTimeout(() => setError(null), 5000);
            }
          } else if (loopPlayback) { 
            // Last episode of last season with loop enabled
            console.log("Looping back to first season");
            setChangingSeasons(true);
            try {
              // Get the first available season when looping
              const firstSeason = availableSeasons[0];
              if (firstSeason) {
                const firstSeasonEpisodes = await fetchAllSeasonEpisodes(playerData.show._id, firstSeason._id);
                setChangingSeasons(false);
                setNavigationLoading(false);
                if (firstSeasonEpisodes?.length > 0) {
                  console.log("Navigating to first episode of first season (loop)");
                  return navigateToEpisode(firstSeasonEpisodes[0], firstSeason, isFullscreen);
                } else {
                  throw new Error("No episodes found in first season");
                }
              } else {
                throw new Error("First season not found");
              }
            } catch (loopErr) {
              console.error("Error looping playback:", loopErr);
              setChangingSeasons(false);
              setNavigationLoading(false);
              setError("Failed to loop playback. Please try again.");
              setTimeout(() => setError(null), 5000);
            }
          } else {
            console.log("Reached end of series, no more episodes");
            setNavigationLoading(false);
          }
        }
    } catch (err) {
        console.error("Error in handleNextEpisode:", err);
        setError("Failed to load next episode. Please try again.");
        setTimeout(() => setError(null), 5000);
        setChangingSeasons(false);
        setNavigationLoading(false);
    }
  }, [playerData, episodeList, loopPlayback, isFullscreen, navigateToEpisode, fetchAllSeasonEpisodes]);
  
  const hasNextSeason = useCallback(() => {
    if (!playerData?.show?.seasons) return false;
    const currentSeasonNumber = playerData.season?.seasonNumber;
    return playerData.show.seasons.some(s => s.seasonNumber > currentSeasonNumber);
  }, [playerData]);
  
  // Update the handleSeasonChange function to handle specific season selection
  const handleSeasonChange = useCallback(async (targetSeasonNumber) => {
    if (!playerData?.show) return;
    
    // Don't do anything if we're already on this season
    if (playerData.season && playerData.season.seasonNumber === targetSeasonNumber) {
      setShowSeasonsMenu(false);
      return;
    }
    
    // loading indicator before starting season change
    setChangingSeasons(true);
    
    try {
      // Find the target season object
      let targetSeason = showDataCache.current.get(`${playerData.show._id}-${targetSeasonNumber}`);
      if (!targetSeason) {
        let showData = apiCache.get('shows', playerData.show._id);
        if (!showData) {
          const response = await ShowsAPI.getById(playerData.show._id);
          if (!response.data.success) throw new Error('Failed to fetch show data');
          showData = response.data.data;
          apiCache.set('shows', playerData.show._id, showData);
        }
        targetSeason = showData.seasons.find(s => s.seasonNumber === targetSeasonNumber);
        if (targetSeason) showDataCache.current.set(`${playerData.show._id}-${targetSeasonNumber}`, targetSeason);
      }

      if (!targetSeason) { 
        setError(`Season ${targetSeasonNumber} not found`); 
        setChangingSeasons(false);
        setShowSeasonsMenu(false);
        return; 
      }

      // Fetch episodes for the target season
      const episodes = await fetchAllSeasonEpisodes(playerData.show._id, targetSeason._id);
      
      // after episodes are loaded
      if (!episodes?.length) { 
        setError(`No episodes found in Season ${targetSeasonNumber}`); 
        setChangingSeasons(false);
        setShowSeasonsMenu(false);
        return; 
      }

      // now we can safely navigate to the episode
      await navigateToEpisode(episodes[0], targetSeason, isFullscreen);
      preloadAdjacentSeasons(playerData.show, targetSeason.seasonNumber);
      setShowSeasonsMenu(false);
      
    } catch (error) {
      console.error("Error changing season:", error);
      setError("Failed to change season. Please try again.");
    } finally {
      // always turn off loading indicator
      setChangingSeasons(false);
    }
  }, [playerData, isFullscreen, navigateToEpisode, preloadAdjacentSeasons, fetchAllSeasonEpisodes]);

  const handlePlayerReady = useCallback(() => setPlayerReady(true), []);
  const handlePlayPause = useCallback(() => {}, []);
  const handleTimeUpdate = useCallback((currentTime, duration) => {
    if (playerData?.id && duration > 0) {
      const progress = Math.floor((currentTime / duration) * 100);
      const now = Date.now();
      
      // Check if we should update based on time (reduced frequency to every 30 seconds) or significant progress change
      if (now - lastProgressUpdateRef.current > 30000 || Math.abs(progress - lastProgressValueRef.current) > 10) {
        lastProgressUpdateRef.current = now;
        lastProgressValueRef.current = progress;
        
        // Make sure we're sending the actual progress not 0
        queueWatchStatusUpdate(playerData.type, playerData.id, progress, progress >= 90);
        
        // Process the queue periodically rather than waiting for the interval
        if (progress % 20 === 0 || progress >= 90) {
          processWatchStatusQueue();
        }
      }
    }
  }, [playerData, queueWatchStatusUpdate, processWatchStatusQueue]);
  const handleVideoEnded = useCallback(() => {
    if (playerData) queueWatchStatusUpdate(playerData.type, playerData.id, 100, true);
    if (playerData?.type === 'episode' && autoplayNext) handleNextEpisode();
  }, [playerData, autoplayNext, handleNextEpisode, queueWatchStatusUpdate]);
  const handleFullscreenToggle = useCallback((isFullscreenActive) => setIsFullscreen(isFullscreenActive), []);
  
  useEffect(() => {
    const watchedSet = new Set();
    try {
      JSON.parse(localStorage.getItem('watchedEpisodes') || '[]').forEach(id => watchedSet.add(id));
    } catch (e) { /* silent */ }
    playerData?.watchHistory?.forEach(item => { if (item.episodeId) watchedSet.add(item.episodeId); });
    if (playerData?.episode?._id) watchedSet.add(playerData.episode._id);
    setWatchedEpisodes(watchedSet);
  }, [playerData]);
  
  useEffect(() => {
    if (!window.placeholderLoaded) {
      const img = new Image(); img.src = '/placeholder.jpg';
      img.onload = () => { window.placeholderLoaded = true; };
    }
    if (!window.imageCache) window.imageCache = {};
  }, []);
  
  // Process the watch status queue periodically
  useEffect(() => {
    // Reduced frequency from 5s to 15s to avoid too many API calls
    const interval = setInterval(processWatchStatusQueue, 15000);
    return () => {
      clearInterval(interval);
      // Process any remaining updates in the queue when unmounting
      if (watchStatusUpdateQueue.current.length > 0) {
        processWatchStatusQueue();
      }
    };
  }, [processWatchStatusQueue]);
  
  useEffect(() => {
    fullscreenAppliedRef.current = false; 
    scrollAttemptedRef.current = false;
    
    if (progressUpdateTimerRef.current) { 
      clearInterval(progressUpdateTimerRef.current); 
      progressUpdateTimerRef.current = null; 
    }
    
    // Set initial loading state
    setLoading(true);
    setLoadingState('initial');
    
    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Set a timeout to handle cases where loading takes too long
    loadingTimeoutRef.current = setTimeout(() => {
      if (loadingState === 'initial' || loadingState === 'loading') {
        console.log("Loading timeout reached, still showing loading indicator");
        
        // If we're still in loading state after the maximum time, try to recover
        if (!playerData) {
          setLoading(false);
          setLoadingState('error');
          setError('Loading timed out. Please refresh or try again later.');
        }
      }
    }, maxLoadingTime);
    
    if (location.state) {
      // Coming from a direct navigation with state
      const data = location.state; 
      setPlayerData(data);
      
      if (data?.episode) currentEpisodeRef.current = data.episode;
      
      if (data?.sources?.length > 0) {
        setVideoSources(data.sources);
        const sourceIndex = Math.min(currentSourceIndex, data.sources.length - 1);
        const sourceUrl = data.sources[sourceIndex].url;
        const isWorkers = isCloudFareLink(sourceUrl);
        setIsWorkersStream(isWorkers);
        
        if (isCloudFareLink(sourceUrl)) {
          setVideoToken(extractTokenFromUrl(sourceUrl) || ''); 
          setUseCustomPlayer(true);
        } else {
          setCurrentUrl(sourceUrl); 
          setUseCustomPlayer(false);
        }
        setCurrentSourceIndex(sourceIndex);
      } else {
        setVideoSources([{ label: 'Default', url: '' }]); 
        setVideoToken(''); 
        setCurrentUrl('');
        setUseCustomPlayer(false); 
        setCurrentSourceIndex(0);
        setIsWorkersStream(false);
      }
      
      startProgressTracking(data); 
      loadSimilarContent(data);
      
      if (data.type === 'episode' && data.show && data.season) {
        fetchAllSeasonEpisodes(data.show._id, data.season._id);
      }
      
      if (data.type === 'episode' && data.episode?._id) {
        setWatchedEpisodes(prev => new Set([...prev, data.episode._id]));
        try {
          const watched = JSON.parse(localStorage.getItem('watchedEpisodes') || '[]');
          if (!watched.includes(data.episode._id)) { 
            watched.push(data.episode._id); 
            localStorage.setItem('watchedEpisodes', JSON.stringify(watched)); 
          }
        } catch(e) {/*silent*/}
      }
      
      if (data.type === 'episode' && data.episode && data.season) {
        preloadAdjacentEpisodes(data.episode, data.season);
      }
      
      if (data.type === 'episode' && data.show && data.season) {
        preloadAdjacentSeasons(data.show, data.season.seasonNumber);
      }
      
      setLoading(false);
      setLoadingState('success');
      
      // Clear the timeout since we have data
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    } else {
      // Coming from direct URL navigation
      loadEpisodeFromUrl()
        .then(success => {
          if (!success && !playerData) {
            // Only show error if we still don't have player data after load attempt
            console.log("URL navigation failed, but keeping loading state until timeout");
            
            // Set another timeout to finally show error if nothing happens
            setTimeout(() => {
              if (!playerData && (loadingState === 'initial' || loadingState === 'loading')) {
                console.error("Failed to load episode from URL after timeout");
                setLoading(false);
                setLoadingState('error');
                setError('Failed to load video data. Please try again or select another video.');
              }
            }, 5000); // Give it 5 more seconds before showing error
          }
        })
        .catch(err => {
          // Handle any unhandled errors from loadEpisodeFromUrl
          console.error("Uncaught error in loadEpisodeFromUrl:", err);
          setLoading(false);
          setLoadingState('error');
          setError('An unexpected error occurred. Please try again later.');
        });
    }
    
    return () => { 
      if (progressUpdateTimerRef.current) clearInterval(progressUpdateTimerRef.current); 
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, [location, navigate, loadEpisodeFromUrl, startProgressTracking, loadSimilarContent, currentSourceIndex, preloadAdjacentEpisodes, preloadAdjacentSeasons, extractTokenFromUrl, isCloudFareLink, fetchAllSeasonEpisodes, loadingState, playerData]);
  
  useEffect(() => {
    if (playerReady && playerData && !fullscreenAppliedRef.current && useCustomPlayer) {
      fullscreenAppliedRef.current = true;
      if (playerData.wasFullscreen && playerRef.current) {
        setTimeout(() => { playerRef.current.requestFullscreen?.(); }, 500);
      }
    }
  }, [playerReady, playerData, useCustomPlayer]);
  
  useEffect(() => {
    if (playerData?.episode && episodeListRef.current && !scrollAttemptedRef.current) {
      scrollAttemptedRef.current = true;
      let scrollSuccess = scrollToCurrentEpisode();
      if (!scrollSuccess) {
        const timer1 = setTimeout(() => {
          scrollSuccess = scrollToCurrentEpisode();
          if (!scrollSuccess) {
            const timer2 = setTimeout(scrollToCurrentEpisode, 500);
            return () => clearTimeout(timer2);
          }
        }, 100);
        return () => clearTimeout(timer1);
      }
    }
  }, [playerData, scrollToCurrentEpisode]);
  
  useEffect(() => {
    const savedEpisodeNumber = localStorage.getItem('scrollToEpisode');
    if (savedEpisodeNumber && episodeListRef.current) {
      localStorage.removeItem('scrollToEpisode');
      setTimeout(() => forceScrollToEpisode(savedEpisodeNumber), 300);
    }
    if (playerData?.episode && episodeListRef.current) {
      setTimeout(() => forceScrollToEpisode(playerData.episode.episodeNumber), 300);
    }
    const savedHeight = localStorage.getItem('episodeListHeight');
    if (savedHeight && episodeListRef.current) episodeListRef.current.style.height = `${savedHeight}px`;
  }, [playerData, forceScrollToEpisode]);
  
  // Track initial progress update
  useEffect(() => {
    if (playerData?.id && currentUser) {
      // Send an initial progress update of 1% to mark that playback started
      queueWatchStatusUpdate(playerData.type, playerData.id, 1, false);
    }
  }, [playerData, currentUser, queueWatchStatusUpdate]);
  
  // Process updates when component unmounts
  useEffect(() => {
    // Clean up when component unmounts
    return () => {
      // Force processing any watch status updates before unmounting
      if (playerData?.id) {
        // Try to get the real progress from the player before unmounting
        let finalProgress = 0;
        try {
          if (playerRef.current) {
            const currentTime = playerRef.current.getCurrentTime();
            const duration = playerRef.current.getDuration();
            if (currentTime && duration) {
              finalProgress = Math.floor((currentTime / duration) * 100);
            }
          }
        } catch (e) {
          console.log('Error getting final progress:', e);
        }
        
        // If we couldn't get progress from player, use last known progress
        if (finalProgress === 0) {
          finalProgress = lastProgressValueRef.current || 0;
        }
        
        // Only update if we have meaningful progress
        if (finalProgress > 0) {
          console.log(`Component unmounting, saving final progress: ${finalProgress}%`);
          queueWatchStatusUpdate(playerData.type, playerData.id, finalProgress, finalProgress >= 90);
        }
      }
      
      // Process all queued updates
      if (watchStatusUpdateQueue.current.length > 0) {
        console.log(`Processing ${watchStatusUpdateQueue.current.length} pending updates before unmount`);
        processWatchStatusQueue();
      }
    };
  }, [playerData, queueWatchStatusUpdate, processWatchStatusQueue]);
  
  // Function to toggle the seasons dropdown menu
  const toggleSeasonsMenu = useCallback(() => {
    setShowSeasonsMenu(prev => !prev);
  }, []);
  
  // Close seasons dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideSeasons = (e) => {
      if (showSeasonsMenu && !e.target.closest('.season-dropdown')) {
        setShowSeasonsMenu(false);
      }
    };
    
    document.addEventListener('click', handleClickOutsideSeasons);
    return () => {
      document.removeEventListener('click', handleClickOutsideSeasons);
    };
  }, [showSeasonsMenu]);
  
  // Render loading state
  if (loadingState === 'initial') {
    return (
      <PageLayoutContainer>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} />
          <div style={{ fontSize: '16px', marginTop: '15px' }}>
            Loading video content...
          </div>
        </LoadingContainer>
      </PageLayoutContainer>
    );
  }

  // Display loading state if we're still loading but not in the initial state
  if (loading && loadingState === 'loading') {
    return (
      <PageLayoutContainer>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} />
          <div style={{ fontSize: '16px', marginTop: '15px' }}>
            Preparing your video...
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-dim)', marginTop: '8px' }}>
            This may take a moment
          </div>
        </LoadingContainer>
      </PageLayoutContainer>
    );
  }

  // Only show error when we're certain there's no data and loading is complete
  if (loadingState === 'error' || (!playerData && !loading)) {
    return (
      <PageLayoutContainer>
        <ErrorMessage>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <div>{error || 'No video data available. Please select a video to watch.'}</div>
          <button onClick={() => navigate('/')}>Go to Home</button>
        </ErrorMessage>
      </PageLayoutContainer>
    );
  }
  
  // Safety check - if we somehow got here without playerData, show loading
  if (!playerData) {
    return (
      <PageLayoutContainer>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} />
          <div style={{ fontSize: '14px', marginTop: '15px' }}>
            Loading content...
          </div>
        </LoadingContainer>
      </PageLayoutContainer>
    );
  }
  
  // Add a safe null check before destructuring playerData
  const isEpisode = playerData?.type === 'episode';
  const displayEpisodes = searchQuery ? filteredEpisodes : episodeList;
  
  // Add ReportButton component after other styled components
  const ActionButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--text-color);
    border: none;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
    
    @media (max-width: 768px) {
      padding: 6px 10px;
      font-size: 13px;
    }
  `;
  
  return (
    <PageLayoutContainer>
      <ContentGrid>
        {/* left panel- Episodes List */}
        <LeftPanel>
          {isEpisode && playerData?.show && playerData?.season ? (
            <EpisodeSidebarSection>
              {playerData.show.seasons && playerData.show.seasons.length > 1 && (
                <SeasonSelector className="season-selector">
                  <SeasonLabel>Season:</SeasonLabel>
                  <SeasonDropdown className="season-dropdown">
                    <SeasonDropdownButton 
                      onClick={toggleSeasonsMenu}
                      title="Select Season"
                    >
                      {changingSeasons ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '12px' }} /> Loading...
                        </span>
                      ) : (
                        <span>Season {playerData.season.seasonNumber}</span>
                      )}
                      <FontAwesomeIcon icon={showSeasonsMenu ? faChevronUp : faChevronDown} />
                    </SeasonDropdownButton>
                    
                    {showSeasonsMenu && (
                      <SeasonsMenu>
                        {playerData.show.seasons
                          .sort((a, b) => a.seasonNumber - b.seasonNumber)
                          .map(season => (
                            <SeasonOption 
                              key={season._id} 
                              $active={playerData.season._id === season._id} 
                              onClick={() => handleSeasonChange(season.seasonNumber)}
                            >
                              Season {season.seasonNumber}
                            </SeasonOption>
                          ))
                        }
                      </SeasonsMenu>
                    )}
                  </SeasonDropdown>
                </SeasonSelector>
              )}
              <EpisodeListHeader className="episode-list-header">
                <EpisodeListTitle>Episodes</EpisodeListTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ViewToggleButton 
                    onClick={toggleViewMode} 
                    $active={viewMode === 'grid'}
                    aria-label={viewMode === 'list' ? 'Switch to grid view' : 'Switch to list view'}
                  >
                    <FontAwesomeIcon icon={viewMode === 'list' ? faThLarge : faList} />
                  </ViewToggleButton>
                  <SearchBox>
                    <SearchInput 
                      type="text" 
                      placeholder="Number or Title" 
                      value={searchQuery} 
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSearch(e.target.value);
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="episode-search-input"
                      id="episode-search-input"
                      aria-label="Search episodes"
                    />
                    <SearchIcon><FontAwesomeIcon icon={faSearch} /></SearchIcon>
                  </SearchBox>
                </div>
              </EpisodeListHeader>
              
              {viewMode === 'list' ? (
                <EpisodeList ref={episodeListRef}>
                  {loadingEpisodes ? (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      padding: '20px',
                      gap: '10px'
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--primary-light)', fontSize: '16px' }} />
                      <span style={{ fontSize: '14px', color: 'var(--text-dim)' }}>Loading...</span>
                    </div>
                  ) : displayEpisodes.length > 0 ? (
                    displayEpisodes.map(episode => {
                      const isCurrentEpisode = playerData.episode && episode._id === playerData.episode._id;
                      const isWatched = isEpisodeWatched(episode._id);
                      return (
                        <EpisodeItem key={episode._id || `episode-${episode.episodeNumber}`} id={`episode-${episode._id || episode.episodeNumber}`}
                          $active={isCurrentEpisode} $watched={isWatched && !isCurrentEpisode}
                          onClick={() => handleEpisodeClick(episode)} className="episode-item">
                          <EpisodeNumber $active={isCurrentEpisode} $watched={isWatched && !isCurrentEpisode} className="episode-number">
                            {episode.episodeNumber}
                          </EpisodeNumber>
                          <EpisodeThumbnail $watched={isWatched && !isCurrentEpisode}>
                            <img src={getImageUrl(episode.image || (playerData.show && playerData.show.image))} alt={episode.title} onError={handleImageError} />
                            {isWatched && !isCurrentEpisode && (<WatchedOverlay><FontAwesomeIcon icon={faCheck} /></WatchedOverlay>)}
                          </EpisodeThumbnail>
                          <EpisodeItemTitle $watched={isWatched && !isCurrentEpisode}>{episode.title || `Episode ${episode.episodeNumber}`}</EpisodeItemTitle>
                          <PlayButton $active={isCurrentEpisode}><FontAwesomeIcon icon={faPlay} /></PlayButton>
                        </EpisodeItem>
                      );
                    })
                  ) : (
                    <NoEpisodesMessage>
                      <FontAwesomeIcon icon={faTimes} /><h4>No episodes found</h4><p>Try a different search term or season</p>
                    </NoEpisodesMessage>
                  )}
                </EpisodeList>
              ) : (
                <GridViewContainer>
                  {loadingEpisodes ? (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      padding: '20px', 
                      gridColumn: '1 / -1', 
                      gap: '10px'
                    }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--primary-light)', fontSize: '16px' }} />
                      <span style={{ fontSize: '14px', color: 'var(--text-dim)' }}>Loading...</span>
                    </div>
                  ) : displayEpisodes.length > 0 ? (
                    displayEpisodes.map(episode => {
                      const isCurrentEpisode = playerData.episode && episode._id === playerData.episode._id;
                      const isWatched = isEpisodeWatched(episode._id);
                      return (
                        <EpisodeGridItem
                          key={episode._id || `episode-${episode.episodeNumber}`}
                          $active={isCurrentEpisode}
                          $watched={isWatched && !isCurrentEpisode}
                          onClick={() => handleEpisodeClick(episode)}
                          role="button"
                          tabIndex="0"
                          onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && handleEpisodeClick(episode)}
                          aria-label={`Play Episode ${episode.episodeNumber}`}
                          title={episode.title || `Episode ${episode.episodeNumber}`}
                        >
                          {episode.episodeNumber}
                        </EpisodeGridItem>
                      );
                    })
                  ) : (
                    <NoEpisodesMessage>
                      <FontAwesomeIcon icon={faTimes} /><h4>No episodes found</h4><p>Try a different search term or season</p>
                    </NoEpisodesMessage>
                  )}
                </GridViewContainer>
              )}
              
              {changingSeasons && (
                <LoadingOverlay>
                  <FontAwesomeIcon icon={faSpinner} />
                </LoadingOverlay>
              )}
            </EpisodeSidebarSection>
          ) : (
            // Display movie info panel when it's a movie
            <MovieInfoPanel>
              <FontAwesomeIcon icon={faInfoCircle} />
              <h3>Movie Information</h3>
              <p>This is a movie, so there are no episodes available. You can adjust playback settings in the player controls.</p>
              <MovieInfoButton onClick={handleBackClick}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Movie Details
              </MovieInfoButton>
            </MovieInfoPanel>
          )}
        </LeftPanel>

        {/* middle panel- Player + Source Info */}
        <MiddlePanel>
          {error && !loading && (
            <ErrorMessage>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div>{error}</div>
              <button onClick={() => setError(null)}>Dismiss</button>
            </ErrorMessage>
          )}
          <PlayerWrapper>
            <VideoContainer>
              {useCustomPlayer ? (
                <Player ref={playerRef} 
                  token={videoToken} 
                  autoPlay={true} 
                  loop={loopPlayback} 
                  autoNext={autoplayNext}
                  title={playerData?.title || 'Video Player'}
                  episodeTitle={playerData?.episodeTitle || (playerData?.episode?.title || '')}
                  episodeNumber={playerData?.episodeNumber || (playerData?.episode?.episodeNumber || null)}
                  seasonNumber={playerData?.seasonNumber || (playerData?.season?.seasonNumber || null)}
                  onReady={handlePlayerReady} 
                  onPlayPause={handlePlayPause} 
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={handleVideoEnded} 
                  onFullscreenToggle={handleFullscreenToggle}
                  onLoopChange={setLoopPlayback} 
                  onAutoNextChange={setAutoplayNext} 
                  onPrevious={handlePrevEpisode} 
                  onNext={handleNextEpisode} 
                />
              ) : (
                <VideoIframe ref={iframeRef} src={currentUrl} allowFullScreen title={playerData?.title || 'Video Player'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation" />
              )}
            </VideoContainer>
            <NavigationButtons>
              <BackButton 
                onClick={handleBackClick} 
                title="Go Back"
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </BackButton>
              {isEpisode && (
                <>
                  <NavButton 
                    onClick={handlePrevEpisode} 
                    disabled={!hasPrevEpisode() || changingSeasons}
                    title="Previous Episode"
                  >
                    <FontAwesomeIcon icon={faBackward} /> Prev
                  </NavButton>
                  <NavButton 
                    onClick={handleNextEpisode} 
                    disabled={!hasNextEpisode() || changingSeasons}
                    title="Next Episode"
                  >
                    <FontAwesomeIcon icon={faForward} /> Next
                  </NavButton>
                </>
              )}
              <ShareButton 
                onClick={handleShareClick}
                title="Share"
              >
                <FontAwesomeIcon icon={faShareAlt} />
              </ShareButton>
              <NavButton 
                onClick={handleOpenReportModal}
                title="Report Issue"
              >
                <FontAwesomeIcon icon={faFlag} />
              </NavButton>
            </NavigationButtons>
          </PlayerWrapper>
          
          {/* Combined playback controls and sources section */}
          <PlaybackControlsSection>
            <ControlsGroup>
              {/* Only show autoplay button for episodes (not movies) */}
              {isEpisode && (
                <PlaybackControlButton 
                  $active={autoplayNext} 
                  onClick={() => { 
                    setAutoplayNext(!autoplayNext); 
                    if (playerRef.current) playerRef.current.setAutoNext?.(!autoplayNext);
                  }}
                  title="Toggle Autoplay"
                >
                  <FontAwesomeIcon icon={faStepForward} /> 
                  {autoplayNext ? 'Auto: On' : 'Auto: Off'}
                </PlaybackControlButton>
              )}
              
              {/* Loop button can be shown for all content types */}
              <PlaybackControlButton 
                $active={loopPlayback} 
                onClick={() => { 
                  setLoopPlayback(!loopPlayback); 
                  if (playerRef.current) playerRef.current.setLoop?.(!loopPlayback);
                }}
                title="Toggle Loop"
              >
                <FontAwesomeIcon icon={faRedo} /> 
                {loopPlayback ? 'Loop: On' : 'Loop: Off'}
              </PlaybackControlButton>
            </ControlsGroup>
            
            {videoSources && videoSources.length > 0 && (
              <SourceDropdown className="source-dropdown">
                <SourceDropdownButton 
                  onClick={toggleSourcesMenu}
                  title="Select Source"
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>
                      {(() => {
                        const currentSource = videoSources[currentSourceIndex];
                        if (!currentSource) return 'Select Source';
                        
                        const dubInfo = extractDubberInfo(currentSource.label);
                        if (dubInfo) {
                          return dubInfo.cleanLabel;
                        }
                        return currentSource.label || `Source ${currentSourceIndex + 1}`;
                      })()}
                    </span>
                    
                    {(() => {
                      const currentSource = videoSources[currentSourceIndex];
                      if (!currentSource) return null;
                      
                      const dubInfo = extractDubberInfo(currentSource.label);
                      if (dubInfo) {
                        return (
                          <DubIconContainer>
                            <UnofficialDubIcon icon={faInfoCircle} />
                            <UnofficialDubTooltip>
                              This is not an official dub. It was dubbed by {dubInfo.dubber}.
                            </UnofficialDubTooltip>
                          </DubIconContainer>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <FontAwesomeIcon icon={showSourcesMenu ? faChevronUp : faChevronDown} />
                </SourceDropdownButton>
                
                {showSourcesMenu && (
                  <SourcesMenu>
                    {videoSources.map((source, index) => {
                      const dubInfo = extractDubberInfo(source.label);
                      const displayLabel = dubInfo ? dubInfo.cleanLabel : (source.label || `Source ${index + 1}`);
                      
                      return (
                        <SourceOption 
                          key={index} 
                          $active={index === currentSourceIndex} 
                          onClick={() => handleSourceChange(source.url, index)}
                          title={source.label || `Source ${index + 1}`}
                        >
                          {displayLabel}
                        </SourceOption>
                      );
                    })}
                  </SourcesMenu>
                )}
              </SourceDropdown>
            )}
          </PlaybackControlsSection>
          
        </MiddlePanel>

        {/* right panel- Movie/Episode Info */}
        <RightPanel>
          <EpisodeInfoSection>
            <div>
              <ContentTitle>{playerData?.title || 'Untitled Video'}</ContentTitle>
              {isEpisode && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '10px' 
                }}>
                  <div>
                    <span style={{ color: 'var(--text-dim)', fontSize: '14px' }}>
                      S{playerData?.seasonNumber || 1} E{playerData?.episodeNumber || 1}
                      {playerData?.episodeTitle ? ` - ${playerData?.episodeTitle}` : ''}
                    </span>
                  </div>
                  <FontAwesomeIcon 
                    icon={faLightbulb} 
                    onClick={handleOpenSuggestionModal}
                    style={{
                      color: '#FFD700', // Golden color
                      cursor: 'pointer',
                      fontSize: '16px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.2)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Suggest changes to title or description"
                  />
                </div>
              )}
              <ReadMoreDescription 
                ref={descriptionRef}
                $expanded={descriptionExpanded} 
                $hasOverflow={hasDescriptionOverflow}
              >
                {playerData?.description || 'No description available.'}
              </ReadMoreDescription>
              {hasDescriptionOverflow && !descriptionExpanded && (
                <ReadMoreButton onClick={() => setDescriptionExpanded(true)}>
                  Read more
                </ReadMoreButton>
              )}
              {hasDescriptionOverflow && descriptionExpanded && (
                <ReadMoreButton onClick={() => setDescriptionExpanded(false)}>
                  Read less
                </ReadMoreButton>
              )}
            </div>
            {similarContent.length > 0 && (
              <SimilarContent>
                <ContentSlider title="You May Also Like" items={similarContent.slice(0, 6)} />
              </SimilarContent>
            )}
          </EpisodeInfoSection>
        </RightPanel>
      </ContentGrid>

      {showSharePopup && (
        <>
          <ShareOverlay onClick={() => setShowSharePopup(false)} />
          <SharePopup>
            <h3><FontAwesomeIcon icon={faShareAlt} /> Share this {playerData?.type === 'movie' ? 'movie' : 'episode'}</h3>
            <CloseButton onClick={() => setShowSharePopup(false)}><FontAwesomeIcon icon={faTimes} /></CloseButton>
            <ShareUrlContainer>
              <input type="text" value={getShareUrl()} readOnly ref={shareUrlRef} />
              <button onClick={handleCopyLink}><FontAwesomeIcon icon={faCopy} /></button>
            </ShareUrlContainer>
            <ShareOptions>
              <button onClick={() => handleShareVia('twitter')}>Twitter</button>
              <button onClick={() => handleShareVia('facebook')}>Facebook</button>
              <button onClick={() => handleShareVia('whatsapp')}>WhatsApp</button>
            </ShareOptions>
          </SharePopup>
        </>
      )}
      {navigationLoading && (
        <NavigationLoadingOverlay>
          <NavigationLoadingContent>
            <FontAwesomeIcon icon={faSpinner} />
            <NavigationLoadingText>Loading episode...</NavigationLoadingText>
          </NavigationLoadingContent>
        </NavigationLoadingOverlay>
      )}
      {showCopyToast && (<Toast>Link copied to clipboard!</Toast>)}

      {/* Add the ReportModal */}
      {playerData && (
        <ReportModal 
          isOpen={showReportModal}
          onClose={handleCloseReportModal}
          contentId={playerData.type === 'episode' ? playerData.showId : (playerData.id || playerData._id)}
          contentType={playerData.type === 'episode' ? 'show' : (playerData.type || (playerData.seasons ? 'show' : 'movie'))}
          contentTitle={playerData.title + (playerData.episodeTitle ? ` - ${playerData.episodeTitle}` : '')}
          sourceUrl={videoSources[currentSourceIndex]?.url || ''}
        />
      )}
      
      {/* Add the EpisodeSuggestionModal */}
      {playerData && playerData.type === 'episode' && (
        <EpisodeSuggestionModal
          isOpen={showSuggestionModal}
          onClose={handleCloseSuggestionModal}
          episodeId={playerData.id || playerData._id}
          episodeTitle={playerData.episodeTitle || ''}
          episodeDescription={playerData.description || ''}
          showTitle={playerData.title || ''}
          seasonNumber={playerData.seasonNumber || 1}
          episodeNumber={playerData.episodeNumber || 1}
          episode={playerData.episode || null}
        />
      )}
    </PageLayoutContainer>
  );
};

const apiCache = {
  episodes: new Map(), shows: new Map(), movies: new Map(), watchStatus: new Map(),
  expirationTime: 5 * 60 * 1000,
  get: function(type, id) {
    const cache = this[type]; if (!cache) return null;
    const cachedItem = cache.get(id); if (!cachedItem) return null;
    if (Date.now() - cachedItem.timestamp > this.expirationTime) { cache.delete(id); return null; }
    return cachedItem.data;
  },
  set: function(type, id, data) {
    const cache = this[type]; if (!cache) return;
    cache.set(id, { data, timestamp: Date.now() });
  }
};

// Add the ViewToggleButton from ShowDetails for episode layout switching
const ViewToggleButton = styled.button`
  background-color: transparent;
  border: none;
  color: var(--text-light);
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 5px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  ${props => props.$active && `
    color: var(--primary-light);
    background-color: rgba(59, 130, 246, 0.1);
  `}
`;

// Add the GridViewContainer for grid layout
const GridViewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  gap: 8px;
  padding: 10px;
  background-color: #0a0a0a;
  
  /* Add scrollbar styling */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #0a0a0a; }
  &::-webkit-scrollbar-thumb { background-color: #333333; border-radius: 3px; }
  scrollbar-width: thin;
  scrollbar-color: #333333 #0a0a0a;
  
  /* Make the grid scrollable */
  overflow-y: auto;
  height: 350px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
    gap: 6px;
    padding: 8px;
  }
  
  @media (min-width: 1024px) {
    height: calc(100% - 95px); /* Account for header elements */
  }
`;

// Add the EpisodeGridItem for grid layout
const EpisodeGridItem = styled.div`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.$active ? 'var(--primary-color)' : props.$watched ? 'rgba(75, 181, 67, 0.2)' : 'rgba(26, 26, 26, 0.8)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-light)' : props.$watched ? 'rgba(75, 181, 67, 0.5)' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$active ? '#fff' : props.$watched ? 'rgba(75, 181, 67, 0.9)' : 'var(--text-light)'};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    background-color: ${props => props.$active ? 'var(--primary-color)' : props.$watched ? 'rgba(75, 181, 67, 0.3)' : 'rgba(42, 42, 42, 0.8)'};
  }
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

export default PlayerPage;

